import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { 
  Megaphone, 
  Sparkles, 
  FileText,
  Video,
  Image as ImageIcon,
  Loader2,
  Edit3,
  Copy,
  Download,
  Upload,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Wand2,
  X,
  Save,
  Plus,
  Trash2,
  Mic,
  User,
  Subtitles,
  Play,
  Eye,
  Volume2,
  Clock,
  Globe,
  Star,
  Pause,
  Type,
  RefreshCw,
  Coins
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useCredits } from '@/context/CreditsContext';
import { CreditCostBadge, InsufficientCreditsAlert } from '@/components/wizard/CreditDisplay';

// Import extracted components and configs
import { 
  PlatformSelector, 
  PLATAFORMAS, 
  getPlatformById, 
  calcularCreditosPlataformas 
} from './components/PlatformSelector';
import { 
  CREATIVE_TEMPLATES, 
  getRecommendedMotor 
} from './components/CreativeConfig';
import { useAuth } from '@/context/AuthContext';

// Import constants from dedicated file
import {
  TIPO_ANUNCIO,
  MOTORES_IMAGEN,
  MOTORES_VIDEO,
  MOTORES_VOZ,
  MOTORES_AVATAR,
  ESTILOS_SUBTITULOS,
  ANGULO_COLORS,
  FORMAT_INFO
} from './constants/trafficConstants';

// ==================== MAIN COMPONENT ====================

const TrafficStep = ({ funnel, brief, assetsConfig }) => {
  const { user, refreshUser, api, uploadApi } = useAuth();
  const { refreshBalance } = useCredits();
  
  // Use ref for api to prevent useEffect re-runs when api reference changes
  const apiRef = useRef(api);
  apiRef.current = api;
  
  // Get limits from assetsConfig (SOURCE OF TRUTH from Strategy step)
  const maxAdCopies = assetsConfig?.adCopies || 4;
  const maxAdCreatives = assetsConfig?.adCreatives || 11;
  
  // Tab state
  const [activeTab, setActiveTab] = useState('copy');
  
  // Fase state
  const [activeFase, setActiveFase] = useState('captacion');
  
  // Platform selection state (multi-select, default Meta Ads)
  const [selectedPlatforms, setSelectedPlatforms] = useState(['meta_ads']);
  const [showPlatformSelector, setShowPlatformSelector] = useState(false);
  
  // Copy states
  const [copiesCaptacion, setCopiesCaptacion] = useState([]);
  const [copiesVenta, setCopiesVenta] = useState([]);
  const [selectedCopyIndex, setSelectedCopyIndex] = useState(null);
  const [generatingCopies, setGeneratingCopies] = useState(false);
  const [regeneratingCopyIndex, setRegeneratingCopyIndex] = useState(null); // Track which single copy is being regenerated
  const [recomendacion, setRecomendacion] = useState('');
  
  // Edit copy inline state
  const [editingCopyIndex, setEditingCopyIndex] = useState(null);
  const [editForm, setEditForm] = useState({ texto_principal: '', headline: '', cta: '' });
  
  // Anuncios state
  const [anuncios, setAnuncios] = useState([]);
  const [anuncioActivo, setAnuncioActivo] = useState(null);
  const [generandoAnuncio, setGenerandoAnuncio] = useState(null);
  const [activeFormats, setActiveFormats] = useState({}); // Track active format per anuncio: { anuncioId: 'feed' | 'story' }
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTipo, setModalTipo] = useState(null);
  const [includeTextOverlay, setIncludeTextOverlay] = useState(true); // Toggle for text overlay on generated images
  const [modalForm, setModalForm] = useState({
    descripcion: '',
    headline: '',
    guion: '',
    estiloSubtitulos: 'hormozi',
    generarStory: true,
    formatoAvatar: '9:16',
    voiceId: 'pFZP5JQG7iQjIQuC4Bku', // Lily Spanish default
    avatarImageBase64: null,
    indicacionesAdicionales: '' // Campo para indicaciones personalizadas del usuario
  });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [suggeringScript, setSuggeringScript] = useState(false);
  
  // Motor selection states
  const [selectedMotorImagen, setSelectedMotorImagen] = useState('flux_pro');
  const [selectedMotorVideo, setSelectedMotorVideo] = useState('kling_pro');
  const [selectedMotorVoz, setSelectedMotorVoz] = useState('elevenlabs');
  const [selectedMotorAvatar, setSelectedMotorAvatar] = useState('hf_speak');
  
  // Creative templates & recommendation
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isDraftMode, setIsDraftMode] = useState(false);
  const recommendedMotor = getRecommendedMotor(brief, isDraftMode);
  
  // Voice/Avatar states
  const [availableVoices, setAvailableVoices] = useState([]);
  const [clonedVoices, setClonedVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [previewingVoice, setPreviewingVoice] = useState(false);
  const [previewAudio, setPreviewAudio] = useState(null);
  const [playingPreview, setPlayingPreview] = useState(false);
  const [durationEstimate, setDurationEstimate] = useState(null);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneForm, setCloneForm] = useState({ name: '', audioBase64: null });
  const [cloningVoice, setCloningVoice] = useState(false);
  const [voiceFilter, setVoiceFilter] = useState('all'); // 'all', 'es', 'en', 'cloned'
  const audioRef = useRef(null);
  
  // Derived state
  const currentCopies = activeFase === 'captacion' ? copiesCaptacion : copiesVenta;
  const setCurrentCopies = activeFase === 'captacion' ? setCopiesCaptacion : setCopiesVenta;
  const selectedCopy = selectedCopyIndex !== null ? currentCopies[selectedCopyIndex] : null;
  
  // Calculate total cost based on selected motors
  const costoTotal = useMemo(() => {
    if (modalTipo === 'generar_imagen') {
      const motorImg = MOTORES_IMAGEN.find(m => m.id === selectedMotorImagen);
      // Higgsfield images don't need Creatomate composition
      const compositionCost = motorImg?.provider === 'Higgsfield' ? 0 : 12;
      return (motorImg?.creditos || 11) + compositionCost;
    }
    if (modalTipo === 'avatar_ia') {
      // Use the selected avatar motor (Higgsfield or HeyGen)
      const motorAvatar = MOTORES_AVATAR.find(m => m.id === selectedMotorAvatar);
      if (motorAvatar?.provider === 'Higgsfield') {
        // Higgsfield avatar: just the avatar cost (includes lip-sync)
        const motorVoz = MOTORES_VOZ.find(m => m.id === selectedMotorVoz);
        return (motorVoz?.creditos || 10) + (motorAvatar?.creditos || 35);
      } else {
        // HeyGen: voice + video generation
        const motorVoz = MOTORES_VOZ.find(m => m.id === selectedMotorVoz);
        const motorVid = MOTORES_VIDEO.find(m => m.id === selectedMotorVideo);
        return (motorVoz?.creditos || 10) + (motorVid?.creditos || 22);
      }
    }
    if (modalTipo === 'subir_imagen') return 12; // Creatomate composition
    if (modalTipo === 'subir_video_subtitulos') return 12; // Creatomate subtitles
    if (modalTipo === 'subir_video_voz') return 22; // Voice + Creatomate
    return 0;
  }, [modalTipo, selectedMotorImagen, selectedMotorVoz, selectedMotorVideo, selectedMotorAvatar]);
  
  // Super admins have unlimited credits
  const isSuperAdmin = user?.role === 'super_admin';
  const creditosInsuficientes = !isSuperAdmin && (user?.credits_balance || 0) < costoTotal;
  
  // Strategy info
  const strategyHasFases = useMemo(() => {
    if (!funnel?.strategy) return false;
    const strategiesWithFases = ['lanzamiento', 'plf', 'desafio', 'eventos'];
    return strategiesWithFases.includes(funnel.strategy);
  }, [funnel?.strategy]);
  
  // Check if only Google Search is selected (no image generation needed)
  const isOnlyGoogleSearch = useMemo(() => {
    return selectedPlatforms.length === 1 && selectedPlatforms[0] === 'google_search';
  }, [selectedPlatforms]);
  
  // Check if YouTube is selected (requires video, not static images)
  const hasYouTubeSelected = useMemo(() => {
    return selectedPlatforms.includes('youtube');
  }, [selectedPlatforms]);
  
  // Check if ONLY YouTube is selected (no image platforms)
  const isOnlyYouTube = useMemo(() => {
    return selectedPlatforms.length === 1 && selectedPlatforms[0] === 'youtube';
  }, [selectedPlatforms]);
  
  // Check if there are image platforms selected (for mixed mode)
  const hasImagePlatforms = useMemo(() => {
    return selectedPlatforms.some(pId => {
      const platform = getPlatformById(pId);
      return platform?.formatos.some(f => f.tipo === 'imagen');
    });
  }, [selectedPlatforms]);
  
  // Get all required formats based on selected platforms
  const requiredFormats = useMemo(() => {
    const formats = [];
    selectedPlatforms.forEach(platformId => {
      const platform = getPlatformById(platformId);
      if (!platform) return;
      platform.formatos.forEach(formato => {
        // Skip text-only formats and logo formats without logo
        if (formato.tipo === 'texto') return;
        if (formato.tipo === 'logo' && !brief?.project_logo && !brief?.logo_url) return;
        // Avoid duplicates (same aspect ratio and type)
        if (!formats.find(f => f.aspect_ratio === formato.aspect_ratio && f.tipo === formato.tipo)) {
          formats.push({
            ...formato,
            platformId,
            platformName: platform.nombre
          });
        }
      });
    });
    return formats;
  }, [selectedPlatforms, brief]);
  
  // Get display formats for carousel - maps aspect ratios to display info
  const getCarouselFormats = useCallback((anuncio) => {
    const formats = [];
    const formatos = anuncio.formatos_generados || {};
    
    // Define format display info
    // Using consistent height container instead of forced aspect ratios to prevent cropping
    const formatInfo = {
      '4:5': { id: 'feed', nombre: 'Feed', aspect: 'h-[280px]', legacy: 'creativo_feed_url' },
      '9:16': { id: 'story', nombre: 'Story/Reel', aspect: 'h-[320px]', legacy: 'creativo_story_url' },
      '1:1': { id: 'cuadrado', nombre: 'Cuadrado', aspect: 'h-[260px]', legacy: null },
      '1.91:1': { id: 'horizontal', nombre: 'Horizontal', aspect: 'h-[200px]', legacy: null },
      '16:9': { id: 'video_16_9', nombre: 'Video 16:9', aspect: 'h-[200px]', legacy: null }
    };
    
    // Add formats from formatos_generados
    Object.entries(formatos).forEach(([ratio, url]) => {
      if (url && formatInfo[ratio]) {
        formats.push({
          ...formatInfo[ratio],
          ratio,
          url,
          isVideo: anuncio.tipo?.includes('video') || anuncio.tipo === 'avatar_ia'
        });
      }
    });
    
    // Fallback to legacy fields if formatos_generados is empty
    if (formats.length === 0) {
      if (anuncio.creativo_feed_url) {
        formats.push({
          ...formatInfo['4:5'],
          ratio: '4:5',
          url: anuncio.creativo_feed_url,
          isVideo: anuncio.tipo?.includes('video') || anuncio.tipo === 'avatar_ia'
        });
      }
      if (anuncio.creativo_story_url) {
        formats.push({
          ...formatInfo['9:16'],
          ratio: '9:16',
          url: anuncio.creativo_story_url,
          isVideo: anuncio.tipo?.includes('video') || anuncio.tipo === 'avatar_ia'
        });
      }
    }
    
    return formats;
  }, []);
  
  // ==================== URL VALIDATION ====================
  const isValidUrl = useCallback((url) => {
    if (!url) return false;
    if (typeof url !== 'string') return false;
    if (url.startsWith('data:')) return true;
    try {
      new URL(url);
      return url.startsWith('http');
    } catch {
      return false;
    }
  }, []);
  
  // ==================== PERSISTENCE ====================
  // NOTA: No guardamos URLs de imágenes en localStorage para evitar QuotaExceededError
  
  // Helper para guardar en localStorage con try/catch
  const guardarEnStorage = useCallback((key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      // Silent fail in production - localStorage may be full or unavailable
      if (process.env.NODE_ENV === 'development') {
        console.warn('[TrafficStep] localStorage write failed:', key, error.message);
      }
    }
  }, []);
  
  // Helper para reducir anuncios (solo metadatos, sin URLs de imágenes)
  const reducirAnunciosParaStorage = useCallback((anunciosArray) => {
    return anunciosArray.map(a => ({
      id: a.id,
      tipo: a.tipo,
      headline: a.headline,
      estado: a.estado === 'generando' ? 'pendiente' : a.estado,
      tiene_feed: !!a.creativo_feed_url,
      tiene_story: !!a.creativo_story_url,
      creado_at: a.creado_at
    }));
  }, []);
  
  // Cargar datos al montar
  useEffect(() => {
    if (!funnel?.id) return;
    
    // Limpiar localStorage corrupto o de versiones anteriores
    try {
      localStorage.removeItem(`traffic_creativos_${funnel.id}`);
    } catch (e) { 
      if (process.env.NODE_ENV === 'development') {
        console.warn('[TrafficStep] localStorage cleanup failed:', e.message);
      }
    }
    
    // Cargar copies de captación - primero de localStorage, luego de funnel.assets
    try {
      const savedCap = localStorage.getItem(`traffic_copies_cap_${funnel.id}`);
      if (savedCap) {
        setCopiesCaptacion(JSON.parse(savedCap));
      } else if (funnel.assets?.adsCopyCaptacion?.length > 0) {
        // Fallback: cargar desde el funnel si existen
        setCopiesCaptacion(funnel.assets.adsCopyCaptacion);
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn('[TrafficStep] Corrupted copies_cap, clearing');
      localStorage.removeItem(`traffic_copies_cap_${funnel.id}`);
      // Try loading from funnel as fallback
      if (funnel.assets?.adsCopyCaptacion?.length > 0) {
        setCopiesCaptacion(funnel.assets.adsCopyCaptacion);
      }
    }
    
    // Cargar copies de venta - primero de localStorage, luego de funnel.assets
    try {
      const savedVen = localStorage.getItem(`traffic_copies_ven_${funnel.id}`);
      if (savedVen) {
        setCopiesVenta(JSON.parse(savedVen));
      } else if (funnel.assets?.adsCopyVenta?.length > 0) {
        // Fallback: cargar desde el funnel si existen
        setCopiesVenta(funnel.assets.adsCopyVenta);
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn('[TrafficStep] Corrupted copies_ven, clearing');
      localStorage.removeItem(`traffic_copies_ven_${funnel.id}`);
      // Try loading from funnel as fallback
      if (funnel.assets?.adsCopyVenta?.length > 0) {
        setCopiesVenta(funnel.assets.adsCopyVenta);
      }
    }
    
    // Cargar anuncios/creativos desde MongoDB (PERSISTENTE)
    const loadCreativosFromDB = async () => {
      try {
        const response = await apiRef.current.get(`/funnels/${funnel.id}/creativos`);
        if (response.data && response.data.length > 0) {
          // Transform DB creativos to local anuncios format
          const loadedAnuncios = response.data.map(creativo => ({
            id: creativo.id,
            tipo: creativo.tipo,
            headline: creativo.headline,
            estado: creativo.estado === 'borrador' ? 'listo' : creativo.estado,
            creativo_feed_url: creativo.formato === '4:5' || creativo.formato === '1:1' ? creativo.asset_url : null,
            creativo_story_url: creativo.formato === '9:16' ? creativo.asset_url : null,
            formato: creativo.formato,
            motor: creativo.motor,
            safe_zone: creativo.safe_zone,
            overlay_config: creativo.overlay_config,
            prompt_output: creativo.prompt_output,
            creado_at: creativo.created_at,
            db_id: creativo.id // Keep reference to DB ID for updates/deletes
          }));
          setAnuncios(loadedAnuncios);
        }
      } catch (e) {
        // If API fails, fall back to localStorage (legacy support)
        if (process.env.NODE_ENV === 'development') {
          console.warn('[TrafficStep] Failed to load creativos from DB, trying localStorage:', e.message);
        }
        try {
          const savedAnuncios = localStorage.getItem(`anuncios_${funnel.id}`);
          if (savedAnuncios) {
            const parsed = JSON.parse(savedAnuncios);
            // Legacy: URLs were null, keep them null
            setAnuncios(parsed.map(a => ({
              ...a,
              creativo_feed_url: null,
              creativo_story_url: null,
              estado: a.estado === 'generando' ? 'pendiente' : a.estado
            })));
          }
        } catch (parseError) {
          if (process.env.NODE_ENV === 'development') console.warn('[TrafficStep] Corrupted anuncios, clearing');
          localStorage.removeItem(`anuncios_${funnel.id}`);
        }
      }
    };
    
    loadCreativosFromDB();
  }, [funnel?.id]); // Only re-run when funnel ID changes
  
  // Guardar copies de captación
  useEffect(() => {
    if (!funnel?.id || copiesCaptacion.length === 0) return;
    guardarEnStorage(`traffic_copies_cap_${funnel.id}`, copiesCaptacion);
  }, [copiesCaptacion, funnel?.id, guardarEnStorage]);
  
  // Guardar copies de venta
  useEffect(() => {
    if (!funnel?.id || copiesVenta.length === 0) return;
    guardarEnStorage(`traffic_copies_ven_${funnel.id}`, copiesVenta);
  }, [copiesVenta, funnel?.id, guardarEnStorage]);
  
  // Guardar anuncios (solo metadatos, sin URLs de imágenes)
  useEffect(() => {
    if (!funnel?.id) return;
    const anunciosReducidos = reducirAnunciosParaStorage(anuncios);
    guardarEnStorage(`anuncios_${funnel.id}`, anunciosReducidos);
  }, [anuncios, funnel?.id, guardarEnStorage, reducirAnunciosParaStorage]);
  
  // ==================== GENERATE COPIES ====================
  
  const handleGenerateCopies = useCallback(async () => {
    if (!funnel?.id) {
      toast.error('Guarda el funnel primero');
      return;
    }
    
    setGeneratingCopies(true);
    
    try {
      // Use maxAdCopies from assetsConfig (default 4, capped at 5 by backend)
      const numToGenerate = Math.min(maxAdCopies, 5);
      
      const response = await apiRef.current.post('/ai/generate/ad-copy-variants', {
        funnel_id: funnel.id,
        pieza_id: `fase_${activeFase}`,
        pieza_nombre: `Copies de ${activeFase}`,
        pieza_tipo: activeFase,
        brief: brief,
        angulos: ['auto'],
        num_copies: numToGenerate
      });
      
      if (response.data?.success && response.data?.copies) {
        setCurrentCopies(response.data.copies);
        setRecomendacion(response.data.recomendacion || '');
        setSelectedCopyIndex(0);
        toast.success(`${response.data.copies.length} copies de ${activeFase} generados`);
        refreshUser?.();
        
        // ==================== PERSIST COPIES TO MONGODB ====================
        // Save immediately after generation
        try {
          const copiesKey = activeFase === 'captacion' ? 'adsCopyCaptacion' : 'adsCopyVenta';
          await apiRef.current.put(`/funnels/${funnel.id}/ad-copies`, {
            [copiesKey]: response.data.copies
          });
        } catch (saveErr) {
          // Don't fail - copies are in memory and localStorage
        }
      } else {
        throw new Error(response.data?.error || 'Error desconocido');
      }
    } catch (error) {
      const msg = error.response?.data?.detail?.error || error.response?.data?.detail || error.message;
      if (msg?.includes('INSUFFICIENT_CREDITS') || error.response?.status === 402) {
        toast.error('Créditos insuficientes', {
          action: { label: 'Comprar', onClick: () => window.location.href = '/billing' }
        });
      } else {
        toast.error(`Error: ${msg}`);
      }
    } finally {
      setGeneratingCopies(false);
      refreshBalance?.();
    }
  }, [funnel?.id, activeFase, maxAdCopies]); // Removed unstable deps: brief object, api, refreshUser, setCurrentCopies, refreshBalance
  
  // ==================== REGENERATE SINGLE COPY ====================
  
  const handleRegenerateSingleCopy = useCallback(async (copyIndex, existingCopy) => {
    if (!funnel?.id) {
      toast.error('Guarda el funnel primero');
      return;
    }
    
    setRegeneratingCopyIndex(copyIndex);
    
    try {
      // Get the existing copy's angle to maintain consistency
      const existingAngulo = existingCopy?.angulo || 'Transformación';
      
      // Generate a single replacement copy with the same angle
      const response = await apiRef.current.post('/ai/generate/ad-copy-variants', {
        funnel_id: funnel.id,
        pieza_id: `fase_${activeFase}_single`,
        pieza_nombre: `Copy individual de ${activeFase}`,
        pieza_tipo: activeFase,
        brief: brief,
        angulos: [existingAngulo], // Request specific angle
        num_copies: 1 // Request only 1 copy
      });
      
      if (response.data?.success && response.data?.copies?.length > 0) {
        const newCopy = response.data.copies[0];
        
        // Update only the specific copy in the array
        const updatedCopies = [...currentCopies];
        updatedCopies[copyIndex] = {
          ...newCopy,
          id: existingCopy?.id || `copy_${Date.now()}` // Preserve ID if exists
        };
        setCurrentCopies(updatedCopies);
        
        toast.success(`Copy #${copyIndex + 1} regenerado con ángulo "${existingAngulo}"`);
        refreshUser?.();
        
        // ==================== PERSIST UPDATED COPIES TO MONGODB ====================
        try {
          const copiesKey = activeFase === 'captacion' ? 'adsCopyCaptacion' : 'adsCopyVenta';
          await apiRef.current.put(`/funnels/${funnel.id}/ad-copies`, {
            [copiesKey]: updatedCopies
          });
        } catch (saveErr) {
          // Don't fail - copies are in memory
        }
      } else {
        throw new Error(response.data?.error || 'No se pudo generar el copy');
      }
    } catch (error) {
      const msg = error.response?.data?.detail?.error || error.response?.data?.detail || error.message;
      if (msg?.includes('INSUFFICIENT_CREDITS') || error.response?.status === 402) {
        toast.error('Créditos insuficientes', {
          action: { label: 'Comprar', onClick: () => window.location.href = '/billing' }
        });
      } else {
        toast.error(`Error al regenerar: ${msg}`);
      }
    } finally {
      setRegeneratingCopyIndex(null);
    }
  }, [funnel?.id, activeFase, currentCopies]); // Removed unstable deps: brief, api, refreshUser, setCurrentCopies
  
  // ==================== MODAL HANDLERS ====================
  
  const openModal = useCallback(() => {
    setModalOpen(true);
    setModalTipo(null);
    setUploadedFile(null);
    setUploadPreview(null);
    setModalForm({
      descripcion: '',
      headline: currentCopies[0]?.headline || currentCopies[0]?.frase || '',
      guion: '',
      estiloSubtitulos: 'hormozi',
      generarStory: true,
      formatoAvatar: '9:16',
      voiceId: 'pFZP5JQG7iQjIQuC4Bku',
      avatarImageBase64: null
    });
    // Reset motor selections to defaults
    setSelectedMotorImagen('flux_pro');
    setSelectedMotorVideo('kling_pro');
    setSelectedMotorVoz('elevenlabs');
  }, [currentCopies]);
  
  const closeModal = useCallback(() => {
    setModalOpen(false);
    setModalTipo(null);
    setUploadedFile(null);
    setUploadPreview(null);
    setPreviewAudio(null);
    setPlayingPreview(false);
    setDurationEstimate(null);
  }, []);
  
  // ==================== VOICE FUNCTIONS ====================
  
  // Load available voices from ElevenLabs
  const loadVoices = useCallback(async () => {
    if (availableVoices.length > 0) return; // Already loaded
    
    setLoadingVoices(true);
    try {
      const res = await apiRef.current.get('/ai/elevenlabs/voices');
      if (res.data?.voices) {
        setAvailableVoices(res.data.voices);
        setClonedVoices(res.data.cloned_voices || []);
      }
    } catch (err) {
      console.error('Error loading voices:', err);
    } finally {
      setLoadingVoices(false);
    }
  }, [api, availableVoices.length]);
  
  // Preview voice with sample text
  const previewVoice = useCallback(async (voiceId, sampleText) => {
    setPreviewingVoice(true);
    try {
      const text = sampleText || modalForm.guion?.slice(0, 100) || '¡Hola! Soy tu asistente de marketing.';
      const res = await apiRef.current.post('/ai/elevenlabs/preview', { text, voice_id: voiceId });
      
      if (res.data?.audio_url) {
        setPreviewAudio(res.data.audio_url);
        // Auto-play preview
        if (audioRef.current) {
          audioRef.current.src = res.data.audio_url;
          audioRef.current.play();
          setPlayingPreview(true);
        }
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || err.message;
      toast.error('Error al previsualizar voz', { description: msg });
    } finally {
      setPreviewingVoice(false);
    }
  }, [api, modalForm.guion]);
  
  // Estimate duration when script changes
  const estimateDuration = useCallback(async (text) => {
    if (!text || text.length < 10) {
      setDurationEstimate(null);
      return;
    }
    try {
      const res = await apiRef.current.post('/ai/elevenlabs/estimate-duration', { text });
      setDurationEstimate(res.data);
    } catch (err) {
      // Fallback local calculation
      const words = text.split(/\s+/).length;
      const seconds = Math.round(words / 2.5);
      setDurationEstimate({
        word_count: words,
        estimated_duration_seconds: seconds,
        estimated_duration_formatted: seconds >= 60 ? `${Math.floor(seconds/60)}:${(seconds%60).toString().padStart(2,'0')}` : `${seconds}s`,
        is_optimal_for_ads: words <= 80,
        recommendation: words <= 80 ? 'Perfecto para anuncios' : `Considera reducir a ~80 palabras (actualmente ${words})`
      });
    }
  }, [api]);
  
  // Clone voice
  const handleCloneVoice = useCallback(async () => {
    if (!cloneForm.name || !cloneForm.audioBase64) {
      toast.error('Necesitas nombre y audio para clonar');
      return;
    }
    
    setCloningVoice(true);
    try {
      const res = await apiRef.current.post('/ai/elevenlabs/clone-voice', {
        name: cloneForm.name,
        description: `Voz clonada - ${cloneForm.name}`,
        audio_base64: cloneForm.audioBase64
      });
      
      if (res.data?.success) {
        toast.success('¡Voz clonada correctamente!');
        setShowCloneModal(false);
        setCloneForm({ name: '', audioBase64: null });
        // Reload voices to include the new cloned voice
        setAvailableVoices([]);
        loadVoices();
        // Auto-select the new voice
        setModalForm(prev => ({ ...prev, voiceId: res.data.voice_id }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al clonar voz');
    } finally {
      setCloningVoice(false);
    }
  }, [api, cloneForm, loadVoices]);
  
  // Delete cloned voice
  const handleDeleteClonedVoice = useCallback(async (voiceId) => {
    if (!window.confirm('¿Eliminar esta voz clonada?')) return;
    
    try {
      await apiRef.current.delete(`/ai/elevenlabs/voice/${voiceId}`);
      toast.success('Voz eliminada');
      setClonedVoices(prev => prev.filter(v => v.voice_id !== voiceId));
      setAvailableVoices([]);
      loadVoices();
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || err.message;
      toast.error('Error al eliminar voz', { description: msg });
    }
  }, [api, loadVoices]);
  
  // Filter voices
  const filteredVoices = useMemo(() => {
    if (voiceFilter === 'cloned') return clonedVoices;
    if (voiceFilter === 'all') return [...clonedVoices, ...availableVoices];
    return availableVoices.filter(v => {
      const lang = v.language?.toLowerCase() || '';
      if (voiceFilter === 'es') return lang.includes('spanish') || lang === 'es';
      if (voiceFilter === 'en') return lang.includes('english') || lang === 'en';
      return true;
    });
  }, [availableVoices, clonedVoices, voiceFilter]);
  
  // Load voices when avatar modal opens
  useEffect(() => {
    if (modalTipo === 'avatar_ia' && availableVoices.length === 0) {
      loadVoices();
    }
  }, [modalTipo, availableVoices.length, loadVoices]);
  
  // Estimate duration when guion changes
  useEffect(() => {
    if (modalTipo === 'avatar_ia' && modalForm.guion) {
      const timer = setTimeout(() => estimateDuration(modalForm.guion), 500);
      return () => clearTimeout(timer);
    }
  }, [modalForm.guion, modalTipo, estimateDuration]);
  
  // Handle audio end
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const handleEnd = () => setPlayingPreview(false);
      audio.addEventListener('ended', handleEnd);
      return () => audio.removeEventListener('ended', handleEnd);
    }
  }, []);
  
  const handleFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const maxSize = modalTipo?.includes('video') ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Archivo demasiado grande (máx ${maxSize / 1024 / 1024}MB)`);
      return;
    }
    
    setUploadedFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => setUploadPreview(e.target?.result);
    reader.readAsDataURL(file);
  }, [modalTipo]);
  
  const handleSuggestScript = useCallback(async () => {
    if (!currentCopies[0]) {
      toast.error('Genera los copies primero');
      return;
    }
    
    setSuggeringScript(true);
    
    try {
      const response = await apiRef.current.post('/ai/generate/suggest-script', {
        product_name: brief?.product_name || brief?.what_you_sell || '',
        copy_frase: currentCopies[0]?.headline || currentCopies[0]?.frase || '',
        copy_cta: currentCopies[0]?.cta || '',
        target_audience: brief?.target_audience || '',
        tono: brief?.tone_of_voice || 'cercano y profesional',
        nicho: brief?.niche || brief?.nicho || ''
      });
      
      if (response.data?.success) {
        setModalForm(prev => ({ ...prev, guion: response.data.script }));
        toast.success('Guión generado');
      } else {
        throw new Error(response.data?.error);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.response?.data?.error || error.message || 'Error desconocido';
      toast.error('Error generando guión', { description: msg });
    } finally {
      setSuggeringScript(false);
    }
  }, [api, brief, currentCopies]);
  
  // ==================== CREATE ANUNCIO ====================
  
  const buildImagePrompt = useCallback(() => {
    const nicho = brief?.niche || brief?.nicho || '';
    const producto = brief?.product_name || brief?.what_you_sell || '';
    const avatar = brief?.target_audience || '';
    const promesa = brief?.main_promise || brief?.promised_result || '';
    
    // Si hay descripción personalizada, usarla como base
    let basePrompt = modalForm.descripcion || 
      `Foto publicitaria profesional para ${producto} en el nicho de ${nicho}. Muestra a ${avatar} consiguiendo ${promesa}. Fondo limpio, iluminación profesional, sin texto.`;
    
    // Si hay indicaciones adicionales, añadirlas al prompt
    if (modalForm.indicacionesAdicionales?.trim()) {
      basePrompt += ` INDICACIONES ADICIONALES: ${modalForm.indicacionesAdicionales}`;
    }
    
    return basePrompt;
  }, [brief, modalForm.descripcion, modalForm.indicacionesAdicionales]);
  
  const handleCrearAnuncio = useCallback(async () => {
    if (!modalForm.headline) {
      toast.error('Escribe un headline');
      return;
    }
    
    // Build list of formats to generate based on selected platforms
    const formatsToGenerate = requiredFormats.filter(f => f.tipo === 'imagen');
    
    const nuevoAnuncio = {
      id: `anuncio_${Date.now()}`,
      tipo: modalTipo,
      headline: modalForm.headline,
      creativo_feed_url: null,
      creativo_story_url: null,
      // New: Store all generated formats
      formatos_generados: {},
      estado: 'generando'
    };
    
    setAnuncios(prev => [...prev, nuevoAnuncio]);
    setGenerandoAnuncio(nuevoAnuncio.id);
    closeModal();
    
    try {
      let feedUrl = null;
      let storyUrl = null;
      let promptOutput = null; // Stores LLM output (PROMPT, NEGATIVE, SAFE_ZONE, OVERLAY)
      const generatedFormats = {}; // Store all generated formats { '4:5': url, '9:16': url, '1.91:1': url }
      
      // Helper function for polling job status
      const pollJobStatus = async (jobId, maxAttempts = 60, intervalMs = 3000) => {
        for (let attempts = 0; attempts < maxAttempts; attempts++) {
          await new Promise(r => setTimeout(r, intervalMs));
          
          try {
            const statusRes = await apiRef.current.get(`/ai/job/${jobId}`);
            
            if (statusRes.data?.status === 'completed' && statusRes.data?.result) {
              return statusRes.data.result;
            } else if (statusRes.data?.status === 'failed') {
              throw new Error(statusRes.data?.error || 'Error en la generación');
            }
            
            // Update progress every 5 polls
            if (attempts % 5 === 0) {
              toast.info(`Generando... (${Math.round(attempts * intervalMs / 1000)}s)`, { id: 'gen-progress' });
            }
          } catch (pollError) {
            if (pollError.response?.status === 404) {
              throw new Error('Job no encontrado');
            }
          }
        }
        throw new Error('Timeout - intenta de nuevo');
      };
      
      if (modalTipo === 'generar_imagen') {
        // Get selected motor configuration (or recommended)
        const effectiveMotor = selectedMotorImagen || recommendedMotor;
        const motorConfig = MOTORES_IMAGEN.find(m => m.id === effectiveMotor) || MOTORES_IMAGEN[0];
        
        // Determine which formats to generate based on selected platforms
        const uniqueAspectRatios = [...new Set(formatsToGenerate.map(f => f.aspect_ratio))];
        const totalFormats = uniqueAspectRatios.length || 1;
        
        toast.info(`Generando ${totalFormats} formato(s) con ${motorConfig.nombre}...`, { id: 'gen-progress' });
        
        let success = false;
        // feedUrl, storyUrl, promptOutput are defined in parent scope
        
        // Build aspect ratio based on template or default
        const aspectRatio = selectedTemplate?.aspect_ratio || '4:5';
        
        try {
          // ==================== NEW 2-PHASE GENERATION ====================
          const headline = modalForm.headline || 
            currentCopies[0]?.headline || 
            currentCopies[0]?.frase || 
            brief?.product_name || '';
          
          const imgRes = await apiRef.current.post('/ai/generate-creative-image', {
            funnel_id: funnel.id,
            headline: headline,
            copy_text: modalForm.descripcion || buildImagePrompt(),
            brief: {
              ...brief,
              // Ensure brand variables are passed
              marca_color_primario: brief?.primary_color,
              marca_color_secundario: brief?.secondary_color,
              marca_color_acento: brief?.accent_color,
              marca_estilo_visual: brief?.marca_estilo_visual,
              marca_evitar: brief?.marca_evitar,
              marca_logo_url: brief?.project_logo || brief?.logo_url
            },
            aspect_ratio: aspectRatio,
            motor: effectiveMotor,
            master_prompt: null, // Uses stored master prompt
            user_instructions: modalForm.indicacionesAdicionales || null,
            template: selectedTemplate ? {
              id: selectedTemplate.id,
              safe_zone: selectedTemplate.safe_zone
            } : null
          });
          
          if (imgRes.data?.success && imgRes.data.image_base64) {
            toast.info('Fase 2: Imagen generada, procesando...', { id: 'gen-progress' });
            
            feedUrl = `data:${imgRes.data.mime_type || 'image/png'};base64,${imgRes.data.image_base64}`;
            promptOutput = imgRes.data.prompt_output;
            success = true;
            
            // Apply headline overlay if enabled (Phase 3)
            if (includeTextOverlay && promptOutput) {
              toast.info('Fase 3: Añadiendo headline...', { id: 'gen-progress' });
              try {
                const overlayRes = await apiRef.current.post('/ai/apply-headline-overlay', {
                  image_base64: imgRes.data.image_base64,
                  headline: headline,
                  safe_zone: promptOutput.safe_zone || selectedTemplate?.safe_zone || 'bottom-third',
                  overlay_config: promptOutput.overlay || {
                    text_color: '#FFFFFF',
                    scrim_type: 'gradient',
                    scrim_color: brief?.primary_color || '#000000',
                    scrim_opacity: 0.7
                  },
                  brand_colors: {
                    primary: brief?.primary_color,
                    secondary: brief?.secondary_color,
                    accent: brief?.accent_color
                  },
                  logo_url: brief?.project_logo || brief?.logo_url || null
                });
                
                if (overlayRes.data?.success && overlayRes.data.image_base64) {
                  feedUrl = `data:${overlayRes.data.mime_type || 'image/png'};base64,${overlayRes.data.image_base64}`;
                  
                  if (overlayRes.data.was_filtered && overlayRes.data.filtered_words?.length > 0) {
                    toast.warning(`Palabras prohibidas filtradas: ${overlayRes.data.filtered_words.join(', ')}`);
                  }
                }
              } catch (overlayErr) {
                console.log('Overlay failed, using clean image:', overlayErr.message);
              }
            }
            
            toast.success(`✅ Creativo generado con ${motorConfig.nombre}`, { id: 'gen-progress' });
          } else if (imgRes.data?.job_id) {
            // Handle async response (job_id)
            const result = await pollJobStatus(imgRes.data.job_id, 40, 3000);
            
            if (result?.success && result.formatos) {
              feedUrl = result.formatos?.['4:5'] || Object.values(result.formatos || {})[0];
              storyUrl = result.formatos?.['9:16'] || null;
              promptOutput = result.prompt_output || imgRes.data.prompt_output || null;
              success = true;
              
              // Apply headline overlay for async results too (if enabled)
              if (includeTextOverlay && feedUrl) {
                toast.info('Añadiendo headline al creativo...', { id: 'gen-progress' });
                try {
                  // Get base64 from feedUrl
                  let overlayBase64 = '';
                  if (feedUrl.startsWith('data:')) {
                    overlayBase64 = feedUrl.split(',')[1];
                  } else {
                    // Fetch and convert to base64
                    const imgResponse = await fetch(feedUrl);
                    const blob = await imgResponse.blob();
                    overlayBase64 = await new Promise((resolve) => {
                      const reader = new FileReader();
                      reader.onloadend = () => resolve(reader.result.split(',')[1]);
                      reader.readAsDataURL(blob);
                    });
                  }
                  
                  if (overlayBase64) {
                    const overlayRes = await apiRef.current.post('/ai/apply-headline-overlay', {
                      image_base64: overlayBase64,
                      headline: headline,
                      safe_zone: promptOutput?.safe_zone || selectedTemplate?.safe_zone || 'bottom-third',
                      overlay_config: promptOutput?.overlay || {
                        text_color: '#FFFFFF',
                        scrim_type: 'gradient',
                        scrim_color: brief?.primary_color || '#000000',
                        scrim_opacity: 0.7
                      },
                      brand_colors: {
                        primary: brief?.primary_color,
                        secondary: brief?.secondary_color,
                        accent: brief?.accent_color
                      }
                    });
                    
                    if (overlayRes.data?.success && overlayRes.data.image_base64) {
                      feedUrl = `data:${overlayRes.data.mime_type || 'image/png'};base64,${overlayRes.data.image_base64}`;
                    }
                  }
                } catch (overlayErr) {
                  console.log('Overlay on async image failed:', overlayErr.message);
                }
              }
              
              toast.success(`Imagen generada con ${motorConfig.nombre}`, { id: 'gen-progress' });
            }
          } else {
            throw new Error(imgRes.data?.error || 'Error en la generación');
          }
        } catch (e) {
          if (process.env.NODE_ENV === 'development') console.log(`${motorConfig.nombre} failed:`, e.message);
          throw e;
        }
        
        if (!success) {
          throw new Error('No se pudo generar la imagen. Intenta otro motor.');
        }
        
        // Store the base format (4:5)
        generatedFormats['4:5'] = feedUrl;
        
        // ==================== BATCH GENERATION: Generate ALL required formats ====================
        // Get base image as base64 for conversions
        let baseImageBase64 = '';
        if (feedUrl.startsWith('data:')) {
          baseImageBase64 = feedUrl.split(',')[1];
        } else {
          try {
            const imgResponse = await fetch(feedUrl);
            const blob = await imgResponse.blob();
            const reader = new FileReader();
            baseImageBase64 = await new Promise((resolve) => {
              reader.onloadend = () => resolve(reader.result.split(',')[1]);
              reader.readAsDataURL(blob);
            });
          } catch (e) {
            console.warn('Could not fetch base image for conversion:', e.message);
          }
        }
        
        // Determine which additional formats we need based on selected platforms
        const additionalFormats = uniqueAspectRatios.filter(ar => ar !== '4:5');
        
        if (additionalFormats.length > 0 && baseImageBase64) {
          toast.info(`Convirtiendo a ${additionalFormats.length} formato(s) adicionales...`, { id: 'gen-progress' });
          
          // Generate all additional formats in parallel
          const conversionPromises = additionalFormats.map(async (targetRatio) => {
            try {
              const convertRes = await apiRef.current.post('/ai/convert-image-format', {
                image_base64: baseImageBase64,
                from_format: '4:5',
                to_format: targetRatio
              });
              
              if (convertRes.data?.success && convertRes.data.image_base64) {
                return { ratio: targetRatio, url: `data:image/png;base64,${convertRes.data.image_base64}` };
              }
            } catch (e) {
              console.warn(`Conversion to ${targetRatio} failed:`, e.message);
            }
            return null;
          });
          
          const results = await Promise.all(conversionPromises);
          results.forEach(result => {
            if (result) {
              generatedFormats[result.ratio] = result.url;
            }
          });
        }
        
        // Map generated formats to legacy feedUrl/storyUrl for backwards compatibility
        feedUrl = generatedFormats['4:5'] || generatedFormats['1:1'] || Object.values(generatedFormats)[0];
        storyUrl = generatedFormats['9:16'] || generatedFormats['4:5'] || feedUrl;
        
        // Log all generated formats
        console.log('[TrafficStep] Generated formats:', Object.keys(generatedFormats));
        toast.success(`✅ ${Object.keys(generatedFormats).length} formato(s) generados`, { id: 'gen-progress' });
        
      } else if (modalTipo === 'subir_imagen') {
        if (!uploadedFile) {
          throw new Error('Sube una imagen primero');
        }
        
        toast.info('Procesando imagen...');
        const base64 = uploadPreview.split(',')[1];
        
        const feedRes = await apiRef.current.post('/ai/process/image-upload', {
          funnel_id: funnel.id,
          image_base64: base64,
          headline: modalForm.headline,
          cta: currentCopies[0]?.cta || '',
          formato: '4:5'
        });
        
        if (feedRes.data?.success) {
          feedUrl = feedRes.data.image_url;
        }
        
        if (modalForm.generarStory) {
          const storyRes = await apiRef.current.post('/ai/process/image-upload', {
            funnel_id: funnel.id,
            image_base64: base64,
            headline: modalForm.headline,
            cta: currentCopies[0]?.cta || '',
            formato: '9:16'
          });
          if (storyRes.data?.success) {
            storyUrl = storyRes.data.image_url;
          }
        }
        
      } else if (modalTipo === 'avatar_ia') {
        if (!modalForm.guion) {
          throw new Error('Escribe un guión para el avatar');
        }
        
        // Get selected motor configurations
        const motorVoz = MOTORES_VOZ.find(m => m.id === selectedMotorVoz) || MOTORES_VOZ[0];
        const motorAvatar = MOTORES_AVATAR.find(m => m.id === selectedMotorAvatar) || MOTORES_AVATAR[0];
        
        toast.info(`Generando Avatar con ${motorVoz.nombre} + ${motorAvatar.nombre}...`, { id: 'avatar-progress' });
        
        // Check if using Higgsfield avatar
        if (motorAvatar.provider === 'Higgsfield') {
          // === HIGGSFIELD FLOW ===
          // Step 1: Generate voice audio first
          let audioUrl = null;
          try {
            const voiceRes = await apiRef.current.post(motorVoz.endpoint, {
              text: modalForm.guion,
              voice_id: modalForm.voiceId || 'pFZP5JQG7iQjIQuC4Bku'
            });
            
            if (voiceRes.data?.success) {
              audioUrl = voiceRes.data.audio_url;
            } else {
              throw new Error(voiceRes.data?.error || 'Error generando voz');
            }
          } catch (voiceError) {
            toast.error(`Error con ${motorVoz.nombre}: ${voiceError.message}`);
            throw voiceError;
          }
          
          toast.info('Generando avatar con Higgsfield...', { id: 'avatar-progress' });
          
          // Step 2: Use Higgsfield Speak endpoint
          const hfPayload = {
            funnel_id: funnel.id,
            image_url: modalForm.avatarImageBase64 || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=512', // Default portrait
            audio_url: audioUrl,
            model: motorAvatar.model || 'speak',
            duration: Math.min(30, Math.ceil(modalForm.guion.split(' ').length / 2.5)) // ~2.5 words/sec
          };
          
          const hfRes = await apiRef.current.post('/ai/higgsfield/avatar', hfPayload);
          
          if (hfRes.data?.success && hfRes.data?.video_url) {
            storyUrl = hfRes.data.video_url;
            feedUrl = hfRes.data.video_url;
            toast.success('Avatar Higgsfield generado correctamente', { id: 'avatar-progress' });
          } else {
            throw new Error(hfRes.data?.error || 'Error generando avatar con Higgsfield');
          }
          
        } else {
          // === HEYGEN + KLING FLOW (Original) ===
          const motorVideo = MOTORES_VIDEO.find(m => m.id === selectedMotorVideo) || MOTORES_VIDEO[0];
          
          // Step 1: Generate voice audio
          let audioUrl = null;
          try {
            const voiceRes = await apiRef.current.post(motorVoz.endpoint, {
              text: modalForm.guion,
              voice_id: modalForm.voiceId || 'pFZP5JQG7iQjIQuC4Bku' // Lily Spanish default
            });
            
            if (voiceRes.data?.success) {
              audioUrl = voiceRes.data.audio_url;
            } else {
              throw new Error(voiceRes.data?.error || 'Error generando voz');
            }
          } catch (voiceError) {
            toast.error(`Error con ${motorVoz.nombre}: ${voiceError.message}`);
            throw voiceError;
          }
          
          // Step 2: Generate video with lip-sync
          const avatarPayload = {
            funnel_id: funnel.id,
            guion: modalForm.guion,
            voice_id: modalForm.voiceId || 'pFZP5JQG7iQjIQuC4Bku',
            formato: modalForm.formatoAvatar || '9:16'
          };
          
          if (modalForm.avatarImageBase64) {
            avatarPayload.avatar_image_base64 = modalForm.avatarImageBase64;
          }
          
          // Use Kling Pro or Turbo based on selection
          const videoEndpoint = motorVideo.id === 'kling_turbo' 
            ? '/ai/generate/kling-turbo' 
            : '/ai/generate/kling-avatar';
          
          const avatarRes = await apiRef.current.post(videoEndpoint, {
            ...avatarPayload,
            prompt_descripcion: `Avatar hablando: ${modalForm.guion.slice(0, 100)}`,
            concepto: modalForm.guion.slice(0, 50),
            target_audience: brief?.target_audience || ''
          });
          
          if (avatarRes.data?.job_id || avatarRes.data?.request_id) {
            const jobId = avatarRes.data.job_id || avatarRes.data.request_id;
            let attempts = 0;
            const maxAttempts = 80;
            
            while (attempts < maxAttempts) {
              await new Promise(r => setTimeout(r, 3000));
              attempts++;
              
              try {
                const statusRes = await apiRef.current.get(`/ai/job/${jobId}`);
                
                if (statusRes.data?.status === 'completed' && statusRes.data?.result) {
                  const result = statusRes.data.result;
                  storyUrl = result.video_url;
                  feedUrl = result.video_url;
                  toast.success('Avatar IA generado correctamente', { id: 'avatar-progress' });
                  break;
                } else if (statusRes.data?.status === 'failed') {
                  throw new Error(statusRes.data?.error || 'Error generando avatar');
                }
                
                if (attempts % 5 === 0) {
                  const elapsed = Math.round(attempts * 3);
                  const step = attempts < 10 ? 'Generando voz...' : 'Sincronizando labios...';
                  toast.info(`${step} (${elapsed}s)`, { id: 'avatar-progress' });
                }
              } catch (pollError) {
                if (pollError.response?.status === 404) {
                  throw new Error('Job de generación no encontrado');
                }
              }
            }
            
            if (!feedUrl) {
              throw new Error('Timeout generando avatar - intenta de nuevo');
            }
          } else if (avatarRes.data?.video_url) {
            storyUrl = avatarRes.data.video_url;
            feedUrl = avatarRes.data.video_url;
          } else {
            throw new Error(avatarRes.data?.error || 'Error generando avatar');
          }
        }
        
      } else if (modalTipo === 'subir_video_subtitulos') {
        if (!uploadedFile) {
          throw new Error('Sube un vídeo primero');
        }
        
        toast.info('Subiendo vídeo...');
        
        // Use uploadApi for large file uploads (5 min timeout)
        const formData = new FormData();
        formData.append('file', uploadedFile);
        
        const tempUploadRes = await uploadApi.post('/ai/upload/video-temp', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            if (percentCompleted % 20 === 0) {
              toast.info(`Subiendo: ${percentCompleted}%`, { id: 'upload-progress' });
            }
          }
        });
        
        if (!tempUploadRes.data?.success || !tempUploadRes.data?.video_url) {
          throw new Error(tempUploadRes.data?.detail || 'Error al subir el vídeo');
        }
        
        const videoUrl = tempUploadRes.data.video_url;
        toast.info('Añadiendo subtítulos (30-60 seg)...');
        
        // Now send the public URL to Creatomate
        const subtitleRes = await apiRef.current.post('/ai/creatomate/add-subtitles', {
          funnel_id: funnel.id,
          video_url: videoUrl,
          style: modalForm.estiloSubtitulos || 'hormozi'
        });
        
        if (subtitleRes.data?.success && subtitleRes.data?.render_id) {
          // Poll for completion
          let attempts = 0;
          const maxAttempts = 40; // 2 min max
          
          while (attempts < maxAttempts) {
            await new Promise(r => setTimeout(r, 3000));
            attempts++;
            
            try {
              const statusRes = await apiRef.current.get(`/ai/creatomate/status/${subtitleRes.data.render_id}`);
              if (statusRes.data?.status === 'succeeded') {
                feedUrl = statusRes.data.url;
                storyUrl = statusRes.data.url;
                toast.success('Subtítulos añadidos correctamente');
                break;
              } else if (statusRes.data?.status === 'failed') {
                throw new Error('Error procesando el vídeo con subtítulos');
              }
              
              // Progress update
              if (attempts % 4 === 0) {
                toast.info(`Procesando subtítulos... (${attempts * 3}s)`, { id: 'subtitle-progress' });
              }
            } catch (e) {
              // Continue polling - transient network errors are expected during long processing
              if (e.message?.includes('Error procesando')) throw e;
              if (process.env.NODE_ENV === 'development') console.log('Polling transient error:', e.message);
            }
          }
          
          if (!feedUrl) {
            throw new Error('Tiempo de espera agotado. Intenta con un vídeo más corto.');
          }
        } else {
          throw new Error(subtitleRes.data?.error || 'Error procesando vídeo');
        }
        
      } else if (modalTipo === 'subir_video_voz') {
        if (!uploadedFile) {
          throw new Error('Sube un vídeo primero');
        }
        if (!modalForm.guion) {
          throw new Error('Escribe el guión para la voz');
        }
        
        toast.info('Subiendo vídeo...');
        
        // Use uploadApi for large file uploads (5 min timeout)
        const formData = new FormData();
        formData.append('file', uploadedFile);
        
        const tempUploadRes = await uploadApi.post('/ai/upload/video-temp', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            if (percentCompleted % 20 === 0) {
              toast.info(`Subiendo: ${percentCompleted}%`, { id: 'upload-progress' });
            }
          }
        });
        
        if (!tempUploadRes.data?.success || !tempUploadRes.data?.video_url) {
          throw new Error(tempUploadRes.data?.detail || 'Error al subir el vídeo');
        }
        
        const videoUrl = tempUploadRes.data.video_url;
        toast.info('Generando voz IA...');
        
        const voiceRes = await apiRef.current.post('/ai/generate/video-with-voice', {
          funnel_id: funnel.id,
          video_url: videoUrl,
          script: modalForm.guion,
          voice_id: modalForm.voiceId || 'pFZP5JQG7iQjIQuC4Bku' // Lily - Spanish default
        });
        
        if (voiceRes.data?.success) {
          feedUrl = voiceRes.data.video_url;
          storyUrl = voiceRes.data.video_url;
          toast.success('Voz IA generada correctamente');
        } else {
          throw new Error(voiceRes.data?.error || 'Error generando voz');
        }
      }
      
      if (!feedUrl && !storyUrl) {
        throw new Error('No se pudo crear el anuncio');
      }
      
      // FIX 4: Debug logs
      console.log('[TrafficStep] feedUrl antes de actualizar:', feedUrl);
      console.log('[TrafficStep] storyUrl antes de actualizar:', storyUrl);
      console.log('[TrafficStep] headline:', modalForm.headline);
      console.log('[TrafficStep] isValidUrl(feedUrl):', isValidUrl(feedUrl));
      console.log('[TrafficStep] isValidUrl(storyUrl):', isValidUrl(storyUrl));
      
      // FIX 1: Validar URLs antes de actualizar
      const validFeedUrl = isValidUrl(feedUrl) ? feedUrl : null;
      const validStoryUrl = isValidUrl(storyUrl) ? storyUrl : (isValidUrl(feedUrl) ? feedUrl : null);
      
      if (!validFeedUrl) {
        console.warn('[TrafficStep] feedUrl inválido, no se actualizará:', feedUrl);
      }
      
      // Update local state
      const anuncioActualizado = {
        ...nuevoAnuncio,
        creativo_feed_url: validFeedUrl,
        creativo_story_url: validStoryUrl,
        estado: validFeedUrl ? 'listo' : 'error',
        headline: modalForm.headline || nuevoAnuncio.headline,
        motor: selectedMotorImagen,
        formato: selectedTemplate?.aspect_ratio || '4:5',
        safe_zone: promptOutput?.safe_zone || 'bottom-third',
        overlay_config: promptOutput?.overlay || null,
        prompt_output: promptOutput,
        // Store all generated formats for multi-platform support
        formatos_generados: generatedFormats || {}
      };
      
      setAnuncios(prev => {
        const nuevosAnuncios = prev.map(a => 
          a.id === nuevoAnuncio.id ? anuncioActualizado : a
        );
        return nuevosAnuncios;
      });
      
      // ==================== PERSIST CREATIVO TO MONGODB ====================
      // Save immediately after successful generation (not on Continue)
      console.log('[TrafficStep] Attempting to save creativo:', { 
        funnelId: funnel?.id, 
        validFeedUrl: !!validFeedUrl,
        feedUrlLength: feedUrl?.length,
        validStoryUrl: !!validStoryUrl 
      });
      
      if (funnel?.id && validFeedUrl) {
        try {
          const creativoData = {
            funnel_id: funnel.id,
            anuncio_numero: anuncios.findIndex(a => a.id === nuevoAnuncio.id) + 1,
            tipo: nuevoAnuncio.tipo || 'imagen',
            formato: selectedTemplate?.aspect_ratio || '4:5',
            motor: selectedMotorImagen,
            headline: modalForm.headline || nuevoAnuncio.headline || '',
            copy_text: modalForm.descripcion || '',
            asset_url: validFeedUrl,
            thumbnail_url: validStoryUrl !== validFeedUrl ? validStoryUrl : null,
            safe_zone: promptOutput?.safe_zone || 'bottom-third',
            overlay_config: promptOutput?.overlay || null,
            prompt_output: promptOutput
          };
          
          // Check if this anuncio already exists in DB (has db_id)
          if (nuevoAnuncio.db_id) {
            // Update existing
            await apiRef.current.put(`/funnels/${funnel.id}/creativos/${nuevoAnuncio.db_id}`, {
              asset_url: validFeedUrl,
              headline: creativoData.headline,
              estado: 'listo'
            });
            console.log('[TrafficStep] Updated creativo in MongoDB:', nuevoAnuncio.db_id);
          } else {
            // Create new
            const saveRes = await apiRef.current.post(`/funnels/${funnel.id}/creativos`, creativoData);
            if (saveRes.data?.id) {
              // Store DB ID in local state for future updates/deletes
              setAnuncios(prev => prev.map(a => 
                a.id === nuevoAnuncio.id ? { ...a, db_id: saveRes.data.id } : a
              ));
              console.log('[TrafficStep] Saved new creativo to MongoDB:', saveRes.data.id);
            }
          }
        } catch (saveErr) {
          console.error('[TrafficStep] Error saving creativo to MongoDB:', saveErr.message);
          // Don't fail the operation - user still has the image in memory
        }
      }
      
      if (validFeedUrl) {
        toast.success('Anuncio creado correctamente');
      } else {
        toast.warning('Anuncio creado pero la imagen no se pudo cargar');
      }
      refreshUser?.();
      refreshBalance?.(); // Update credit balance display
      
    } catch (error) {
      // FIX: Handle object errors properly to avoid [object Object]
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' 
        ? detail 
        : (detail?.error || detail?.message || error.message || 'Error desconocido');
      
      setAnuncios(prev => prev.map(a => 
        a.id === nuevoAnuncio.id 
          ? { ...a, estado: 'error' }
          : a
      ));
      
      if (msg?.includes('INSUFFICIENT_CREDITS') || error.response?.status === 402) {
        toast.error('Créditos insuficientes', {
          action: { label: 'Comprar', onClick: () => window.location.href = '/billing' }
        });
      } else {
        toast.error(`Error: ${msg}`);
      }
    } finally {
      setGenerandoAnuncio(null);
    }
  }, [modalTipo, modalForm, uploadedFile, uploadPreview, funnel?.id, brief, currentCopies, buildImagePrompt, closeModal, refreshUser, api, selectedMotorImagen, selectedMotorVideo, selectedMotorVoz, isValidUrl, requiredFormats, anuncios, recommendedMotor, uploadApi, selectedMotorAvatar, selectedTemplate, includeTextOverlay]);
  
  // ==================== ANUNCIO ACTIONS ====================
  
  const eliminarAnuncio = useCallback(async (id) => {
    if (window.confirm('¿Eliminar este anuncio?')) {
      // Find the anuncio to get its DB ID
      const anuncio = anuncios.find(a => a.id === id);
      
      // Remove from local state immediately (optimistic update)
      setAnuncios(prev => prev.filter(a => a.id !== id));
      if (anuncioActivo === id) setAnuncioActivo(null);
      
      // Delete from MongoDB if it exists there
      if (anuncio?.db_id && funnel?.id) {
        try {
          await apiRef.current.delete(`/funnels/${funnel.id}/creativos/${anuncio.db_id}`);
        } catch (err) {
          // Don't revert - user wanted to delete anyway
        }
      }
      
      // Also remove from localStorage (legacy cleanup)
      try {
        const savedAnuncios = localStorage.getItem(`anuncios_${funnel?.id}`);
        if (savedAnuncios) {
          const parsed = JSON.parse(savedAnuncios);
          const filtered = parsed.filter(a => a.id !== id);
          localStorage.setItem(`anuncios_${funnel?.id}`, JSON.stringify(filtered));
        }
      } catch (e) {
        // Ignore localStorage errors
      }
      
      toast.success('Anuncio eliminado');
    }
  }, [anuncioActivo, anuncios, funnel?.id]); // Removed api - using apiRef
  
  const descargarCreativo = useCallback((url, nombre) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = nombre;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);
  
  const updateAnuncioHeadline = useCallback((id, newHeadline) => {
    setAnuncios(prev => prev.map(a => 
      a.id === id ? { ...a, headline: newHeadline } : a
    ));
  }, []);
  
  // ==================== RENDER ====================
  
  if (!funnel) {
    return (
      <div className="p-6 rounded-xl bg-yellow-500/10 border border-yellow-500/30 text-center" data-testid="traffic-step">
        <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-yellow-400" />
        <p className="text-yellow-400 font-medium">Cargando datos del funnel...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="traffic-step">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text-main flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-brand/20 to-purple-500/20 border border-brand/30">
              <Megaphone className="w-6 h-6 text-brand-500" />
            </div>
            Creativos Publicitarios
          </h2>
          <p className="text-text-secondary mt-1">
            Genera copies y creativos para tus anuncios de Meta Ads
          </p>
        </div>
        
        <div className="flex items-center gap-2 text-sm">
          <span className="text-text-muted">Créditos:</span>
          <span className="font-bold text-brand-500">{isSuperAdmin ? '∞' : (user?.credits_balance?.toLocaleString() || 0)}</span>
        </div>
      </div>
      
      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-surface border border-border-subtle">
          <TabsTrigger value="copy" className="data-[state=active]:bg-brand/20 data-[state=active]:text-brand-500" data-testid="tab-copy">
            <FileText className="w-4 h-4 mr-2" />
            Copies
            {(copiesCaptacion.length > 0 || copiesVenta.length > 0) && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded-full">
                {copiesCaptacion.length + copiesVenta.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="creativos" className="data-[state=active]:bg-brand/20 data-[state=active]:text-brand-500" data-testid="tab-creativos">
            <Sparkles className="w-4 h-4 mr-2" />
            Creativos
            {anuncios.filter(a => a.estado === 'listo').length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded-full">
                {anuncios.filter(a => a.estado === 'listo').length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
        
        {/* ==================== TAB: COPIES ==================== */}
        <TabsContent value="copy" className="mt-6">
          <div className="grid grid-cols-12 gap-6">
            {/* Left Column - Copy List */}
            <div className="col-span-4 space-y-4">
              {strategyHasFases && (
                <div className="flex gap-2 p-1 bg-surface rounded-lg border border-border-subtle">
                  <button
                    onClick={() => { setActiveFase('captacion'); setSelectedCopyIndex(copiesCaptacion.length > 0 ? 0 : null); }}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                      activeFase === 'captacion' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'text-text-muted hover:text-text-main'
                    }`}
                  >
                    Captación
                  </button>
                  <button
                    onClick={() => { setActiveFase('venta'); setSelectedCopyIndex(copiesVenta.length > 0 ? 0 : null); }}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                      activeFase === 'venta' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'text-text-muted hover:text-text-main'
                    }`}
                  >
                    Venta
                  </button>
                </div>
              )}
              
              <div className="bg-surface rounded-xl border border-border-subtle p-4">
                <h3 className="text-sm font-medium text-text-main mb-3 flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${activeFase === 'captacion' ? 'bg-amber-400' : 'bg-green-400'}`} />
                  Copies de {activeFase}
                </h3>
                
                {currentCopies.length > 0 ? (
                  <div className="space-y-2">
                    {currentCopies.map((copy, index) => (
                      <div
                        key={copy.id || `copy-${index}`}
                        className={`p-3 rounded-lg transition-all ${
                          selectedCopyIndex === index ? 'bg-brand/20 border border-brand/40' : 'bg-app-bg border border-border-subtle hover:border-brand/30'
                        }`}
                      >
                        {editingCopyIndex === index ? (
                          <div className="space-y-3">
                            <Textarea
                              value={editForm.texto_principal}
                              onChange={(e) => setEditForm(prev => ({ ...prev, texto_principal: e.target.value }))}
                              className="bg-app-bg border-border-subtle text-sm resize-none"
                              rows={2}
                              placeholder="Texto del anuncio"
                            />
                            <Input
                              value={editForm.headline}
                              onChange={(e) => setEditForm(prev => ({ ...prev, headline: e.target.value }))}
                              className="bg-app-bg border-border-subtle text-sm"
                              placeholder="Headline"
                            />
                            <Input
                              value={editForm.cta}
                              onChange={(e) => setEditForm(prev => ({ ...prev, cta: e.target.value }))}
                              className="bg-app-bg border-border-subtle text-sm"
                              placeholder="CTA"
                            />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => {
                                const updatedCopies = [...currentCopies];
                                updatedCopies[index] = { ...updatedCopies[index], ...editForm };
                                setCurrentCopies(updatedCopies);
                                setEditingCopyIndex(null);
                                toast.success('Copy actualizado');
                              }} className="flex-1 bg-brand-500 hover:bg-brand/90">
                                <Save className="w-3 h-3 mr-1" /> Guardar
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingCopyIndex(null)} className="flex-1">
                                <X className="w-3 h-3 mr-1" /> Cancelar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full text-left">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-text-muted">#{index + 1}</span>
                              <div className="flex items-center gap-1">
                                <span className={`text-xs px-2 py-0.5 rounded-full border ${ANGULO_COLORS[copy.angulo] || 'bg-gray-500/20 text-gray-400'}`}>
                                  {copy.angulo}
                                </span>
                                {/* Regenerate single copy button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRegenerateSingleCopy(index, copy);
                                  }}
                                  disabled={regeneratingCopyIndex === index}
                                  className="p-1 hover:bg-amber-500/20 rounded transition-colors disabled:opacity-50"
                                  title="Regenerar este copy"
                                >
                                  {regeneratingCopyIndex === index ? (
                                    <Loader2 className="w-3 h-3 text-amber-400 animate-spin" />
                                  ) : (
                                    <RefreshCw className="w-3 h-3 text-text-muted hover:text-amber-400" />
                                  )}
                                </button>
                                {/* Edit copy button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditForm({
                                      texto_principal: copy.texto_principal || copy.frase || '',
                                      headline: copy.headline || copy.frase || '',
                                      cta: copy.cta || ''
                                    });
                                    setEditingCopyIndex(index);
                                  }}
                                  className="p-1 hover:bg-brand/20 rounded transition-colors"
                                  title="Editar manualmente"
                                >
                                  <Edit3 className="w-3 h-3 text-text-muted hover:text-brand-500" />
                                </button>
                              </div>
                            </div>
                            <div 
                              onClick={() => setSelectedCopyIndex(index)} 
                              className="cursor-pointer hover:bg-card-hover/50 rounded p-1 -mx-1"
                            >
                              <p className="text-sm text-text-main line-clamp-2">{copy.headline || copy.frase}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-10 h-10 mx-auto text-text-muted mb-2" />
                    <p className="text-sm text-text-muted">Sin copies generados</p>
                  </div>
                )}
                
                <Button
                  onClick={handleGenerateCopies}
                  disabled={generatingCopies}
                  className="w-full mt-4 bg-gradient-to-r from-brand to-purple-500"
                  data-testid="btn-generate-copies"
                >
                  {generatingCopies ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando...</> : <><Wand2 className="w-4 h-4 mr-2" /> Generar 5 copies <CreditCostBadge operation="copy_variants" className="ml-2" /></>}
                </Button>
                
                {recomendacion && <p className="mt-3 text-xs text-text-muted italic border-l-2 border-brand/30 pl-2">{recomendacion}</p>}
              </div>
            </div>
            
            {/* Right Column - Copy Preview */}
            <div className="col-span-8">
              {selectedCopy ? (
                <div className="bg-surface rounded-xl border border-border-subtle p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${ANGULO_COLORS[selectedCopy.angulo] || 'bg-gray-500/20 text-gray-400'}`}>
                      {selectedCopy.angulo}
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => {
                      navigator.clipboard.writeText(`${selectedCopy.texto_principal || ''}\n\n${selectedCopy.headline || selectedCopy.frase || ''}\n\n${selectedCopy.cta || ''}`);
                      toast.success('Copiado');
                    }}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="mb-4">
                    <label className="text-xs font-medium text-text-muted mb-2 block uppercase tracking-wide">Texto del anuncio</label>
                    <p className="text-base text-text-main leading-relaxed">{selectedCopy.texto_principal || selectedCopy.frase || 'Sin texto'}</p>
                  </div>
                  
                  <div className="mb-4 p-4 bg-brand/10 rounded-lg border border-brand/20">
                    <label className="text-xs font-medium text-brand-500 mb-2 block uppercase tracking-wide">Headline</label>
                    <p className="text-xl font-bold text-text-main">{selectedCopy.headline || selectedCopy.frase}</p>
                  </div>
                  
                  <div className="mb-4">
                    <label className="text-xs font-medium text-text-muted mb-2 block uppercase tracking-wide">CTA</label>
                    <p className="text-lg text-brand-500 font-medium">{selectedCopy.cta}</p>
                  </div>
                  
                  <div className="pt-4 border-t border-border-subtle">
                    <p className="text-xs text-text-muted flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      Este copy se usará en todos los anuncios
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-surface rounded-xl border border-border-subtle p-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-text-muted mb-4" />
                  <h3 className="text-lg font-medium text-text-main mb-2">{currentCopies.length > 0 ? 'Selecciona un copy' : 'Genera los copies primero'}</h3>
                  <p className="text-text-secondary mb-4">Los 5 copies se usarán para testear todos tus anuncios</p>
                  {currentCopies.length === 0 && (
                    <Button onClick={handleGenerateCopies} disabled={generatingCopies} className="bg-gradient-to-r from-brand to-purple-500">
                      <Wand2 className="w-4 h-4 mr-2" /> Generar ahora
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
        
        {/* ==================== TAB: CREATIVOS ==================== */}
        <TabsContent value="creativos" className="mt-6">
          {currentCopies.length === 0 ? (
            <div className="bg-surface rounded-xl border border-border-subtle p-12 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-amber-400 mb-4" />
              <h3 className="text-lg font-medium text-text-main mb-2">Primero genera los copies</h3>
              <p className="text-text-secondary mb-6">Necesitas tener copies para crear anuncios</p>
              <Button onClick={() => setActiveTab('copy')} className="bg-gradient-to-r from-amber-500 to-orange-500">
                <ChevronRight className="w-4 h-4 mr-2 rotate-180" /> Ir a generar copies
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Copies Preview */}
              <div className="bg-surface rounded-xl border border-border-subtle p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-text-main">Los 5 copies que se usarán en todos los anuncios</h3>
                  {strategyHasFases && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => setActiveFase('captacion')}
                        className={`px-2 py-1 text-xs rounded ${activeFase === 'captacion' ? 'bg-amber-500/20 text-amber-400' : 'text-text-muted'}`}
                      >
                        Captación
                      </button>
                      <button
                        onClick={() => setActiveFase('venta')}
                        className={`px-2 py-1 text-xs rounded ${activeFase === 'venta' ? 'bg-green-500/20 text-green-400' : 'text-text-muted'}`}
                      >
                        Venta
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {currentCopies.map((copy, i) => (
                    <span key={copy.id || i} className="px-3 py-1.5 bg-app-bg rounded-lg text-xs text-text-main border border-border-subtle">
                      {copy.headline || copy.frase}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* ==================== PLATFORM SELECTOR ==================== */}
              <PlatformSelector 
                selectedPlatforms={selectedPlatforms}
                setSelectedPlatforms={setSelectedPlatforms}
                brief={brief}
              />
              
              {/* Anuncios Grid */}
              <div className="grid grid-cols-2 gap-4">
                {anuncios.map((anuncio, index) => (
                  <div
                    key={anuncio.id}
                    className={`bg-surface rounded-2xl border transition-all ${
                      anuncio.estado === 'listo' ? 'border-green-500/30' : 
                      anuncio.estado === 'error' ? 'border-red-500/30' : 
                      'border-border-subtle'
                    } ${anuncioActivo === anuncio.id ? 'ring-2 ring-brand/50' : ''}`}
                  >
                    {/* Card Header */}
                    <div className="p-4 border-b border-border-subtle flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-text-main">Anuncio {index + 1}</span>
                        <span className="text-xs px-2 py-0.5 bg-surface border border-border-subtle rounded">
                          {TIPO_ANUNCIO.find(t => t.id === anuncio.tipo)?.nombre || anuncio.tipo}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {anuncio.estado === 'listo' && <CheckCircle2 className="w-4 h-4 text-green-400" />}
                        {anuncio.estado === 'generando' && <Loader2 className="w-4 h-4 text-brand-500 animate-spin" />}
                        {anuncio.estado === 'error' && <AlertCircle className="w-4 h-4 text-red-400" />}
                        <button onClick={() => eliminarAnuncio(anuncio.id)} className="p-1 hover:bg-red-500/10 rounded">
                          <Trash2 className="w-4 h-4 text-text-muted hover:text-red-400" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Previews - Dynamic Carousel based on formatos_generados */}
                    <div className="p-4">
                      {anuncio.estado === 'generando' ? (
                        <div className="aspect-[4/5] bg-app-bg rounded-lg animate-pulse flex items-center justify-center">
                          <Loader2 className="w-8 h-8 text-brand-500 animate-spin" />
                        </div>
                      ) : (() => {
                        const carouselFormats = getCarouselFormats(anuncio);
                        const activeIndex = activeFormats[anuncio.id] || 0;
                        
                        if (carouselFormats.length === 0) {
                          return (
                            <div className="aspect-[4/5] bg-app-bg rounded-lg border-2 border-dashed border-border-subtle flex flex-col items-center justify-center gap-2">
                              <ImageIcon className="w-8 h-8 text-text-muted" />
                              <span className="text-xs text-text-muted">Sin creativo</span>
                            </div>
                          );
                        }
                        
                        return (
                          <div className="relative">
                            {/* Carousel Container */}
                            <div 
                              className="overflow-hidden rounded-lg touch-pan-x"
                              onTouchStart={(e) => {
                                const touch = e.touches[0];
                                e.currentTarget.dataset.startX = touch.clientX;
                              }}
                              onTouchEnd={(e) => {
                                const startX = parseFloat(e.currentTarget.dataset.startX || '0');
                                const endX = e.changedTouches[0].clientX;
                                const diff = startX - endX;
                                if (Math.abs(diff) > 50) {
                                  const newIndex = diff > 0 
                                    ? Math.min(activeIndex + 1, carouselFormats.length - 1)
                                    : Math.max(activeIndex - 1, 0);
                                  setActiveFormats(prev => ({ ...prev, [anuncio.id]: newIndex }));
                                }
                              }}
                            >
                              <div 
                                className="flex transition-transform duration-300 ease-out"
                                style={{ transform: `translateX(${-activeIndex * 100}%)` }}
                              >
                                {carouselFormats.map((format, formatIdx) => (
                                  <div 
                                    key={format.id} 
                                    className={`min-w-full ${format.aspect} bg-app-bg relative group flex items-center justify-center p-2`}
                                  >
                                    {format.isVideo ? (
                                      <video 
                                        src={format.url} 
                                        className="max-w-full max-h-full object-contain rounded" 
                                        controls
                                        playsInline
                                        preload="metadata"
                                        onError={(e) => {
                                          console.warn('[TrafficStep] Video load error:', format.url);
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    ) : format.url ? (
                                      <img 
                                        src={format.url} 
                                        alt={`${format.nombre} ${format.ratio}`}
                                        className="max-w-full max-h-full object-contain cursor-pointer rounded"
                                        onClick={() => setAnuncioActivo(anuncioActivo === anuncio.id ? null : anuncio.id)}
                                        onError={(e) => {
                                          console.warn('[TrafficStep] Image load error:', format.url);
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    ) : (
                                      <div className="w-full h-full bg-surface flex items-center justify-center">
                                        <ImageIcon className="w-8 h-8 text-text-muted" />
                                      </div>
                                    )}
                                    
                                    {/* Hover overlay with actions */}
                                    {format.url && (
                                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 pointer-events-none group-hover:pointer-events-auto">
                                        <button 
                                          onClick={() => setAnuncioActivo(anuncioActivo === anuncio.id ? null : anuncio.id)} 
                                          className="p-2 bg-white/20 rounded-full hover:bg-white/30"
                                        >
                                          <Eye className="w-4 h-4 text-white" />
                                        </button>
                                        <button 
                                          onClick={() => descargarCreativo(format.url, `anuncio-${index + 1}-${format.id}.${format.isVideo ? 'mp4' : 'jpg'}`)} 
                                          className="p-2 bg-white/20 rounded-full hover:bg-white/30"
                                        >
                                          <Download className="w-4 h-4 text-white" />
                                        </button>
                                      </div>
                                    )}
                                    
                                    {/* Format label */}
                                    <span className="absolute bottom-2 left-2 text-xs px-2 py-0.5 bg-black/60 text-white rounded">
                                      {format.nombre} {format.ratio}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* Navigation Arrows - only show if more than 1 format */}
                            {carouselFormats.length > 1 && (
                              <>
                                <button
                                  onClick={() => setActiveFormats(prev => ({ ...prev, [anuncio.id]: Math.max(0, activeIndex - 1) }))}
                                  className={`absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 hover:bg-black/70 transition-opacity ${
                                    activeIndex === 0 ? 'opacity-30 pointer-events-none' : 'opacity-100'
                                  }`}
                                >
                                  <ChevronLeft className="w-4 h-4 text-white" />
                                </button>
                                <button
                                  onClick={() => setActiveFormats(prev => ({ ...prev, [anuncio.id]: Math.min(carouselFormats.length - 1, activeIndex + 1) }))}
                                  className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 hover:bg-black/70 transition-opacity ${
                                    activeIndex === carouselFormats.length - 1 ? 'opacity-30 pointer-events-none' : 'opacity-100'
                                  }`}
                                >
                                  <ChevronRight className="w-4 h-4 text-white" />
                                </button>
                              </>
                            )}
                            
                            {/* Dots Indicator - only show if more than 1 format */}
                            {carouselFormats.length > 1 && (
                              <div className="flex justify-center gap-2 mt-2">
                                {carouselFormats.map((format, formatIdx) => (
                                  <button
                                    key={format.id}
                                    onClick={() => setActiveFormats(prev => ({ ...prev, [anuncio.id]: formatIdx }))}
                                    className={`w-2 h-2 rounded-full transition-colors ${
                                      activeIndex === formatIdx ? 'bg-brand-500' : 'bg-white/30'
                                    }`}
                                    title={`${format.nombre} (${format.ratio})`}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      
                      {/* Headline */}
                      <div className="mt-3">
                        <Input
                          value={anuncio.headline}
                          onChange={(e) => updateAnuncioHeadline(anuncio.id, e.target.value)}
                          className="bg-app-bg border-border-subtle text-sm"
                          placeholder="Headline del anuncio"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Add Anuncio Card */}
                {anuncios.length < maxAdCreatives ? (
                  <button
                    onClick={openModal}
                    data-testid="btn-anadir-anuncio"
                    className="bg-surface rounded-2xl border-2 border-dashed border-border-subtle hover:border-brand/50 p-8 flex flex-col items-center justify-center gap-3 transition-all min-h-[280px]"
                  >
                    <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center">
                      <Plus className="w-6 h-6 text-brand-500" />
                    </div>
                    <span className="text-text-main font-medium">Añadir nuevo anuncio</span>
                    <span className="text-xs text-text-muted">{anuncios.length}/{maxAdCreatives} creativos</span>
                  </button>
                ) : (
                  <div
                    className="bg-surface/50 rounded-2xl border-2 border-dashed border-border-subtle p-8 flex flex-col items-center justify-center gap-3 min-h-[280px] opacity-60"
                  >
                    <div className="w-12 h-12 rounded-xl bg-surface flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-green-500" />
                    </div>
                    <span className="text-text-muted font-medium">Límite alcanzado</span>
                    <span className="text-xs text-text-muted">{maxAdCreatives}/{maxAdCreatives} creativos</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
      
      {/* ==================== MODAL: CREAR ANUNCIO ==================== */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl bg-app-bg border-border-subtle p-0" style={{ maxHeight: 'calc(100vh - 48px)' }} aria-describedby="modal-crear-anuncio-desc">
          <div className="flex flex-col h-full max-h-[calc(100vh-48px)]">
            <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0 border-b border-border-subtle">
              <DialogTitle className="text-text-main">
                {modalTipo ? TIPO_ANUNCIO.find(t => t.id === modalTipo)?.nombre : '¿Cómo quieres crear este anuncio?'}
              </DialogTitle>
              <p id="modal-crear-anuncio-desc" className="sr-only">
                Elige el tipo de creativo que quieres generar para tu anuncio
              </p>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto px-6 py-4">
          {!modalTipo ? (
            /* Type Selection */
            <div className="grid gap-3 mt-4">
              {/* Warning if only Google Search is selected */}
              {isOnlyGoogleSearch && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-400">Solo Google Search seleccionado</p>
                      <p className="text-xs text-amber-400/80 mt-1">
                        Google Search es solo texto, no requiere imágenes. Selecciona Meta Ads o Google Display para crear creativos visuales.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Platforms summary with required formats */}
              {!isOnlyGoogleSearch && requiredFormats.length > 0 && (
                <div className="p-3 bg-brand/5 border border-brand/20 rounded-xl">
                  <p className="text-xs text-text-muted mb-2">Se generarán {requiredFormats.length} formato(s):</p>
                  <div className="flex flex-wrap gap-1.5">
                    {requiredFormats.map((f) => (
                      <span key={`${f.platformId}-${f.aspect_ratio}`} className="text-xs px-2 py-1 bg-brand/10 text-brand rounded-full">
                        {f.nombre} ({f.aspect_ratio})
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {TIPO_ANUNCIO.filter(tipo => {
                // If only Google Search is selected, only show non-image types or hide all visual types
                if (isOnlyGoogleSearch) {
                  return !['generar_imagen', 'subir_imagen', 'subir_video_subtitulos', 'subir_video_voz', 'avatar_ia'].includes(tipo.id);
                }
                // If ONLY YouTube is selected, hide static image generation (YouTube = video only)
                if (isOnlyYouTube) {
                  return !['generar_imagen', 'subir_imagen'].includes(tipo.id);
                }
                return true;
              }).map((tipo) => {
                const Icon = tipo.icon;
                // Special handling: if YouTube is selected along with other platforms, 
                // warn that generar_imagen won't create YouTube content
                const youtubeWarning = hasYouTubeSelected && hasImagePlatforms && tipo.id === 'generar_imagen';
                
                return (
                  <button
                    key={tipo.id}
                    onClick={() => setModalTipo(tipo.id)}
                    className="p-4 bg-surface rounded-xl border border-border-subtle hover:border-brand/50 text-left flex items-center gap-4 transition-all"
                  >
                    <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                      <Icon className="w-6 h-6 text-brand-500" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-text-main">{tipo.nombre}</span>
                        {tipo.badge && (
                          <span className={`text-xs px-2 py-0.5 rounded ${tipo.badge === 'Recomendado' ? 'bg-green-500/20 text-green-400' : 'bg-surface text-text-muted'}`}>
                            {tipo.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-text-muted">{tipo.descripcion}</p>
                      {youtubeWarning && (
                        <p className="text-xs text-amber-400 mt-1">
                          ⚠️ Para YouTube usa Avatar IA o Subir Video
                        </p>
                      )}
                    </div>
                    {tipo.creditos !== null && (
                      <span className="text-sm font-medium text-brand-500">{tipo.creditos} cr</span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            /* Type Form */
            <div className="space-y-4 mt-4">
              {/* Back button */}
              <button onClick={() => setModalTipo(null)} className="text-sm text-text-muted hover:text-text-main flex items-center gap-1">
                <ChevronRight className="w-4 h-4 rotate-180" /> Cambiar tipo
              </button>
              
              {/* GENERAR IMAGEN */}
              {modalTipo === 'generar_imagen' && (
                <>
                  {/* SELECTOR DE MOTOR DE IMAGEN */}
                  <div>
                    <label className="text-sm font-medium text-text-main mb-3 block">Elige motor de generación</label>
                    <div className="grid grid-cols-3 gap-2">
                      {MOTORES_IMAGEN.map(motor => (
                        <button
                          key={motor.id}
                          onClick={() => setSelectedMotorImagen(motor.id)}
                          disabled={!motor.disponible}
                          className={`p-3 rounded-xl border text-left transition-all ${
                            selectedMotorImagen === motor.id 
                              ? 'bg-brand/10 border-brand/50 ring-1 ring-brand/30' 
                              : 'bg-surface border-border-subtle hover:border-brand/30'
                          } ${!motor.disponible ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium">{motor.badge}</span>
                            <span className="text-xs font-bold text-brand">{motor.creditos} cr</span>
                          </div>
                          <p className="text-sm font-medium text-text-main">{motor.nombre}</p>
                          <p className="text-xs text-text-muted">{motor.descripcion}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-text-main mb-2 block">Describe el creativo (opcional)</label>
                    <Textarea
                      value={modalForm.descripcion}
                      onChange={(e) => setModalForm(prev => ({ ...prev, descripcion: e.target.value }))}
                      placeholder="Ej: Mujer profesional trabajando con laptop en oficina moderna"
                      className="bg-surface border-border-subtle"
                      rows={2}
                    />
                    <p className="text-xs text-text-muted mt-1">Si lo dejas vacío, usaremos los datos del brief para crear el prompt automáticamente</p>
                  </div>
                  
                  {/* Campo de indicaciones adicionales */}
                  <div>
                    <label className="text-sm font-medium text-text-main mb-2 block">
                      Indicaciones adicionales <span className="text-text-muted font-normal">(opcional)</span>
                    </label>
                    <Textarea
                      value={modalForm.indicacionesAdicionales}
                      onChange={(e) => setModalForm(prev => ({ ...prev, indicacionesAdicionales: e.target.value }))}
                      placeholder="Ej: Colores cálidos, estilo minimalista, que se vea la marca, fondo degradado azul..."
                      className="bg-surface border-border-subtle"
                      rows={2}
                    />
                    <p className="text-xs text-text-muted mt-1">Estas indicaciones se añadirán al prompt maestro del brief</p>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="genStory"
                      checked={modalForm.generarStory}
                      onChange={(e) => setModalForm(prev => ({ ...prev, generarStory: e.target.checked }))}
                      className="rounded border-border-subtle"
                    />
                    <label htmlFor="genStory" className="text-sm text-text-main">Generar también en 9:16 Story</label>
                  </div>
                  
                  {/* Toggle para incluir texto en la imagen */}
                  <div className="flex items-center justify-between p-3 bg-surface rounded-xl border border-border-subtle">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${includeTextOverlay ? 'bg-brand/20' : 'bg-background'}`}>
                        <Type className={`w-4 h-4 ${includeTextOverlay ? 'text-brand' : 'text-text-muted'}`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-main">Incluir texto en la imagen</p>
                        <p className="text-xs text-text-muted">
                          {includeTextOverlay 
                            ? 'El headline se añadirá sobre la imagen' 
                            : 'Imagen limpia sin texto'}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIncludeTextOverlay(!includeTextOverlay)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${
                        includeTextOverlay ? 'bg-brand' : 'bg-gray-300'
                      }`}
                    >
                      <span 
                        className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                          includeTextOverlay ? 'translate-x-6' : 'translate-x-1'
                        }`} 
                      />
                    </button>
                  </div>
                </>
              )}
              
              {/* SUBIR IMAGEN */}
              {modalTipo === 'subir_imagen' && (
                <>
                  <div>
                    <label className="text-sm font-medium text-text-main mb-2 block">Sube tu imagen</label>
                    <label className="block">
                      <div className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${uploadPreview ? 'border-green-500/50' : 'border-border-subtle hover:border-brand/50'}`}>
                        {uploadPreview ? (
                          <img src={uploadPreview} alt="Preview" className="max-h-40 mx-auto rounded-lg" />
                        ) : (
                          <>
                            <Upload className="w-8 h-8 mx-auto text-text-muted mb-2" />
                            <p className="text-sm text-text-muted">JPG, PNG, WebP (máx 10MB)</p>
                          </>
                        )}
                      </div>
                      <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                    </label>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-main mb-2 block">Título / Headline</label>
                    <Textarea
                      value={modalForm.headline}
                      onChange={(e) => setModalForm(prev => ({ ...prev, headline: e.target.value }))}
                      placeholder="Ej: Transforma tu negocio en 30 días"
                      className="bg-surface border-border-subtle"
                      rows={2}
                    />
                    <p className="text-xs text-text-muted mt-1">Este texto aparecerá sobre tu imagen</p>
                  </div>
                  
                  {/* Indicaciones para composición */}
                  <div>
                    <label className="text-sm font-medium text-text-main mb-2 block">
                      Indicaciones de diseño <span className="text-text-muted font-normal">(opcional)</span>
                    </label>
                    <Textarea
                      value={modalForm.indicacionesAdicionales}
                      onChange={(e) => setModalForm(prev => ({ ...prev, indicacionesAdicionales: e.target.value }))}
                      placeholder="Ej: Texto en blanco con sombra, posición superior, logo en esquina..."
                      className="bg-surface border-border-subtle"
                      rows={2}
                    />
                    <p className="text-xs text-text-muted mt-1">Cómo quieres que se vea el texto sobre la imagen</p>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="genStory2"
                      checked={modalForm.generarStory}
                      onChange={(e) => setModalForm(prev => ({ ...prev, generarStory: e.target.checked }))}
                      className="rounded border-border-subtle"
                    />
                    <label htmlFor="genStory2" className="text-sm text-text-main">Generar versión 9:16 automáticamente</label>
                  </div>
                </>
              )}
              
              {/* VIDEO + SUBTITULOS o VOZ */}
              {(modalTipo === 'subir_video_subtitulos' || modalTipo === 'subir_video_voz') && (
                <>
                  <div>
                    <label className="text-sm font-medium text-text-main mb-2 block">Sube tu vídeo</label>
                    <label className="block">
                      <div className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${uploadedFile ? 'border-green-500/50' : 'border-border-subtle hover:border-brand/50'}`}>
                        {uploadedFile ? (
                          <div className="flex items-center justify-center gap-2">
                            <Video className="w-6 h-6 text-green-400" />
                            <span className="text-sm text-green-400">{uploadedFile.name}</span>
                          </div>
                        ) : (
                          <>
                            <Video className="w-8 h-8 mx-auto text-text-muted mb-2" />
                            <p className="text-sm text-text-muted">MP4, MOV (máx 50MB)</p>
                          </>
                        )}
                      </div>
                      <input type="file" accept="video/*" onChange={handleFileUpload} className="hidden" />
                    </label>
                  </div>
                  
                  {modalTipo === 'subir_video_voz' && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-text-main">Guión para la voz en off</label>
                        <Button size="sm" variant="ghost" onClick={handleSuggestScript} disabled={suggeringScript}>
                          {suggeringScript ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                          Sugerir guión
                        </Button>
                      </div>
                      <Textarea
                        value={modalForm.guion}
                        onChange={(e) => setModalForm(prev => ({ ...prev, guion: e.target.value }))}
                        placeholder="Escribe lo que dirá la voz..."
                        className="bg-surface border-border-subtle"
                        rows={4}
                      />
                    </div>
                  )}
                  
                  <div>
                    <label className="text-sm font-medium text-text-main mb-2 block">Estilo de subtítulos</label>
                    <div className="flex gap-2">
                      {ESTILOS_SUBTITULOS.map(est => (
                        <button
                          key={est.id}
                          onClick={() => setModalForm(prev => ({ ...prev, estiloSubtitulos: est.id }))}
                          className={`flex-1 p-3 rounded-lg border transition-all ${modalForm.estiloSubtitulos === est.id ? 'bg-brand/20 border-brand/40' : 'bg-surface border-border-subtle'}`}
                        >
                          <span className="text-sm font-medium text-text-main">{est.nombre}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Indicaciones para subtítulos/voz */}
                  <div>
                    <label className="text-sm font-medium text-text-main mb-2 block">
                      Indicaciones adicionales <span className="text-text-muted font-normal">(opcional)</span>
                    </label>
                    <Textarea
                      value={modalForm.indicacionesAdicionales}
                      onChange={(e) => setModalForm(prev => ({ ...prev, indicacionesAdicionales: e.target.value }))}
                      placeholder={modalTipo === 'subir_video_subtitulos' 
                        ? "Ej: Color de subtítulos amarillo, posición centrada, tamaño grande..."
                        : "Ej: Tono entusiasta, pausas dramáticas, énfasis en beneficios..."
                      }
                      className="bg-surface border-border-subtle"
                      rows={2}
                    />
                    <p className="text-xs text-text-muted mt-1">
                      {modalTipo === 'subir_video_subtitulos' 
                        ? "Personaliza cómo se verán los subtítulos"
                        : "Indicaciones para el estilo de la voz en off"
                      }
                    </p>
                  </div>
                </>
              )}
              
              {/* AVATAR IA */}
              {modalTipo === 'avatar_ia' && (
                <>
                  {/* Hidden audio element for previews */}
                  <audio ref={audioRef} className="hidden" />
                  
                  {/* SELECTOR DE MOTOR DE VOZ */}
                  <div>
                    <label className="text-sm font-medium text-text-main mb-3 block">Motor de voz</label>
                    <div className="grid grid-cols-2 gap-2">
                      {MOTORES_VOZ.map(motor => (
                        <button
                          key={motor.id}
                          onClick={() => setSelectedMotorVoz(motor.id)}
                          disabled={!motor.disponible}
                          className={`p-3 rounded-xl border text-left transition-all ${
                            selectedMotorVoz === motor.id 
                              ? 'bg-brand/10 border-brand/50 ring-1 ring-brand/30' 
                              : 'bg-surface border-border-subtle hover:border-brand/30'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium">{motor.badge}</span>
                            <span className="text-xs font-bold text-brand">{motor.creditos} cr</span>
                          </div>
                          <p className="text-sm font-medium text-text-main">{motor.nombre}</p>
                          <p className="text-xs text-text-muted">{motor.descripcion}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* SELECTOR DE MOTOR DE AVATAR (Higgsfield vs HeyGen) */}
                  <div>
                    <label className="text-sm font-medium text-text-main mb-3 block">Motor de avatar</label>
                    <div className="grid grid-cols-3 gap-2">
                      {MOTORES_AVATAR.map(motor => (
                        <button
                          key={motor.id}
                          onClick={() => setSelectedMotorAvatar(motor.id)}
                          disabled={!motor.disponible}
                          className={`p-3 rounded-xl border text-left transition-all ${
                            selectedMotorAvatar === motor.id 
                              ? 'bg-brand/10 border-brand/50 ring-1 ring-brand/30' 
                              : 'bg-surface border-border-subtle hover:border-brand/30'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium">{motor.badge}</span>
                            <span className="text-xs font-bold text-brand">{motor.creditos} cr</span>
                          </div>
                          <p className="text-sm font-medium text-text-main">{motor.nombre}</p>
                          <p className="text-xs text-text-muted">{motor.descripcion}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* SELECTOR DE MOTOR DE VIDEO - Solo mostrar para HeyGen */}
                  {selectedMotorAvatar === 'heygen' && (
                  <div>
                    <label className="text-sm font-medium text-text-main mb-3 block">Motor de video (para HeyGen)</label>
                    <div className="grid grid-cols-2 gap-2">
                      {MOTORES_VIDEO.filter(m => !m.provider || m.provider === 'fal.ai').map(motor => (
                        <button
                          key={motor.id}
                          onClick={() => setSelectedMotorVideo(motor.id)}
                          disabled={!motor.disponible}
                          className={`p-3 rounded-xl border text-left transition-all ${
                            selectedMotorVideo === motor.id 
                              ? 'bg-brand/10 border-brand/50 ring-1 ring-brand/30' 
                              : 'bg-surface border-border-subtle hover:border-brand/30'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium">{motor.badge}</span>
                            <span className="text-xs font-bold text-brand">{motor.creditos} cr</span>
                          </div>
                          <p className="text-sm font-medium text-text-main">{motor.nombre}</p>
                          <p className="text-xs text-text-muted">{motor.descripcion}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  )}
                  
                  {/* Script Section */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-text-main">Guión del avatar</label>
                      <Button size="sm" variant="ghost" onClick={handleSuggestScript} disabled={suggeringScript}>
                        {suggeringScript ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                        Sugerir guión
                      </Button>
                    </div>
                    <Textarea
                      value={modalForm.guion}
                      onChange={(e) => setModalForm(prev => ({ ...prev, guion: e.target.value }))}
                      placeholder="Escribe lo que dirá el avatar (máx 80 palabras para 30s)..."
                      className="bg-surface border-border-subtle"
                      rows={4}
                    />
                    
                    {/* Duration Estimate */}
                    {durationEstimate && (
                      <div className={`mt-2 p-2 rounded-lg flex items-center justify-between text-xs ${
                        durationEstimate.is_optimal_for_ads ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400'
                      }`}>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {durationEstimate.estimated_duration_formatted} · {durationEstimate.word_count} palabras
                        </span>
                        <span>{durationEstimate.is_optimal_for_ads ? '✓ Óptimo' : '⚠️ Muy largo'}</span>
                      </div>
                    )}
                    <p className="text-xs text-text-muted mt-1">El avatar hablará con voz IA y lip-sync real</p>
                  </div>
                  
                  {/* Indicaciones adicionales para avatar */}
                  <div>
                    <label className="text-sm font-medium text-text-main mb-2 block">
                      Indicaciones de estilo <span className="text-text-muted font-normal">(opcional)</span>
                    </label>
                    <Textarea
                      value={modalForm.indicacionesAdicionales}
                      onChange={(e) => setModalForm(prev => ({ ...prev, indicacionesAdicionales: e.target.value }))}
                      placeholder="Ej: Tono amigable y cercano, pausas dramáticas, enfatizar los beneficios..."
                      className="bg-surface border-border-subtle"
                      rows={2}
                    />
                    <p className="text-xs text-text-muted mt-1">Indicaciones para el estilo de presentación del avatar</p>
                  </div>
                  
                  {/* Voice Selection - Only for ElevenLabs */}
                  {selectedMotorVoz === 'elevenlabs' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-text-main">Voz de ElevenLabs</label>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => setShowCloneModal(true)}
                        className="text-brand"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Clonar mi voz
                      </Button>
                    </div>
                    
                    {/* Voice Filter Tabs */}
                    <div className="flex gap-1 mb-3">
                      {[
                        { id: 'all', label: 'Todas', icon: Volume2 },
                        { id: 'es', label: '🇪🇸 Español', icon: Globe },
                        { id: 'en', label: '🇬🇧 Inglés', icon: Globe },
                        { id: 'cloned', label: '⭐ Mis voces', icon: Star }
                      ].map(filter => (
                        <button
                          key={filter.id}
                          onClick={() => setVoiceFilter(filter.id)}
                          className={`px-2 py-1 text-xs rounded-full transition-all ${
                            voiceFilter === filter.id 
                              ? 'bg-brand/20 text-brand border border-brand/40' 
                              : 'bg-surface/50 text-text-muted hover:bg-surface'
                          }`}
                        >
                          {filter.label}
                        </button>
                      ))}
                    </div>
                    
                    {/* Voice Grid */}
                    {loadingVoices ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-brand" />
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                        {filteredVoices.slice(0, 12).map(voice => (
                          <button
                            key={voice.voice_id}
                            onClick={() => setModalForm(prev => ({ ...prev, voiceId: voice.voice_id }))}
                            className={`p-2 rounded-lg border text-left transition-all group relative ${
                              modalForm.voiceId === voice.voice_id 
                                ? 'bg-brand/20 border-brand/40' 
                                : 'bg-surface border-border-subtle hover:border-brand/30'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-sm font-medium text-text-main flex items-center gap-1">
                                  {voice.category === 'cloned' && <Star className="w-3 h-3 text-amber-400" />}
                                  {voice.name}
                                </span>
                                <span className="text-xs text-text-muted block">
                                  {voice.gender === 'female' ? '♀' : voice.gender === 'male' ? '♂' : ''} {voice.language || ''}
                                </span>
                              </div>
                              
                              {/* Preview Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (playingPreview && modalForm.voiceId === voice.voice_id) {
                                    audioRef.current?.pause();
                                    setPlayingPreview(false);
                                  } else {
                                    previewVoice(voice.voice_id);
                                  }
                                }}
                                disabled={previewingVoice}
                                className="p-1 rounded-full bg-surface/80 hover:bg-brand/20 opacity-0 group-hover:opacity-100 transition-all"
                              >
                                {previewingVoice ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : playingPreview && modalForm.voiceId === voice.voice_id ? (
                                  <Pause className="w-3 h-3" />
                                ) : (
                                  <Play className="w-3 h-3" />
                                )}
                              </button>
                            </div>
                            
                            {/* Delete button for cloned voices */}
                            {voice.category === 'cloned' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClonedVoice(voice.voice_id);
                                }}
                                className="absolute -top-1 -right-1 p-1 rounded-full bg-red-500/80 opacity-0 group-hover:opacity-100 transition-all"
                              >
                                <X className="w-2 h-2 text-white" />
                              </button>
                            )}
                          </button>
                        ))}
                        
                        {filteredVoices.length === 0 && (
                          <p className="col-span-2 text-center text-text-muted text-sm py-4">
                            {voiceFilter === 'cloned' ? 'No tienes voces clonadas aún' : 'No hay voces disponibles'}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Selected voice preview button */}
                    {modalForm.voiceId && modalForm.guion && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => previewVoice(modalForm.voiceId, modalForm.guion.slice(0, 100))}
                        disabled={previewingVoice}
                        className="mt-2 w-full"
                      >
                        {previewingVoice ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Play className="w-3 h-3 mr-1" />
                        )}
                        Escuchar preview del guión
                      </Button>
                    )}
                  </div>
                  )}
                  
                  {/* Optional Avatar Image */}
                  <div>
                    <label className="text-sm font-medium text-text-main mb-2 block">
                      Foto del avatar <span className="text-text-muted font-normal">(opcional)</span>
                    </label>
                    <label className="block">
                      <div className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all ${
                        modalForm.avatarImageBase64 ? 'border-green-500/50' : 'border-border-subtle hover:border-brand/50'
                      }`}>
                        {modalForm.avatarImageBase64 ? (
                          <div className="flex items-center justify-center gap-3">
                            <img 
                              src={modalForm.avatarImageBase64} 
                              alt="Avatar" 
                              className="w-16 h-16 rounded-full object-cover"
                            />
                            <div className="text-left">
                              <p className="text-sm text-text-main font-medium">Foto cargada</p>
                              <button 
                                onClick={(e) => { e.preventDefault(); setModalForm(prev => ({ ...prev, avatarImageBase64: null })); }}
                                className="text-xs text-red-400 hover:underline"
                              >
                                Eliminar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <User className="w-8 h-8 mx-auto text-text-muted mb-2" />
                            <p className="text-sm text-text-muted">Sube una foto para el lip-sync</p>
                            <p className="text-xs text-text-muted">Cara frontal, buena iluminación</p>
                          </>
                        )}
                      </div>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file && file.size < 5 * 1024 * 1024) {
                            const reader = new FileReader();
                            reader.onload = (ev) => setModalForm(prev => ({ ...prev, avatarImageBase64: ev.target?.result }));
                            reader.readAsDataURL(file);
                          } else if (file) {
                            toast.error('La imagen debe ser menor a 5MB');
                          }
                        }} 
                        className="hidden" 
                      />
                    </label>
                  </div>
                  
                  {/* Format Selection */}
                  <div>
                    <label className="text-sm font-medium text-text-main mb-2 block">Formato</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setModalForm(prev => ({ ...prev, formatoAvatar: '9:16' }))}
                        className={`flex-1 p-3 rounded-lg border transition-all ${modalForm.formatoAvatar === '9:16' ? 'bg-brand/20 border-brand/40' : 'bg-surface border-border-subtle'}`}
                      >
                        <span className="text-sm font-medium text-text-main">9:16 Story</span>
                        <span className="text-xs text-text-muted block">Recomendado</span>
                      </button>
                      <button
                        onClick={() => setModalForm(prev => ({ ...prev, formatoAvatar: '16:9' }))}
                        className={`flex-1 p-3 rounded-lg border transition-all ${modalForm.formatoAvatar === '16:9' ? 'bg-brand/20 border-brand/40' : 'bg-surface border-border-subtle'}`}
                      >
                        <span className="text-sm font-medium text-text-main">16:9 Horizontal</span>
                        <span className="text-xs text-text-muted block">YouTube</span>
                      </button>
                      <button
                        onClick={() => setModalForm(prev => ({ ...prev, formatoAvatar: '1:1' }))}
                        className={`flex-1 p-3 rounded-lg border transition-all ${modalForm.formatoAvatar === '1:1' ? 'bg-brand/20 border-brand/40' : 'bg-surface border-border-subtle'}`}
                      >
                        <span className="text-sm font-medium text-text-main">1:1 Cuadrado</span>
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-xs text-amber-400 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    El avatar puede tardar 2-3 minutos (voz ElevenLabs + lip-sync Kling)
                  </p>
                </>
              )}
              
              {/* Headline (común a todos) */}
              <div>
                <label className="text-sm font-medium text-text-main mb-2 block">Headline del creativo</label>
                <Input
                  value={modalForm.headline}
                  onChange={(e) => setModalForm(prev => ({ ...prev, headline: e.target.value }))}
                  placeholder="El texto que irá en el creativo..."
                  className="bg-surface border-border-subtle"
                />
              </div>
              
              {/* COSTE TOTAL */}
              {modalTipo && (
                <div className={`p-3 rounded-lg border ${
                  creditosInsuficientes 
                    ? 'bg-red-500/10 border-red-500/30' 
                    : 'bg-brand/10 border-brand/20'
                }`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-text-main">
                      Coste total:
                      <span className={`font-bold ml-2 ${creditosInsuficientes ? 'text-red-400' : 'text-brand'}`}>
                        {costoTotal} créditos
                      </span>
                    </p>
                    <p className="text-xs text-text-muted">
                      Tienes: {isSuperAdmin ? '∞' : (user?.credits_balance || 0)} cr
                    </p>
                  </div>
                  {creditosInsuficientes && (
                    <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Créditos insuficientes · 
                      <a href="/billing" className="underline hover:text-red-300">Comprar más →</a>
                    </p>
                  )}
                </div>
              )}
              
              {/* Create Button */}
            </div>
          )}
            </div>
            
            {/* Fixed Footer with Create Button */}
            {modalTipo && (
              <div className="px-6 py-4 border-t border-border-subtle flex-shrink-0 bg-app-bg">
                <Button
                  onClick={handleCrearAnuncio}
                  disabled={generandoAnuncio !== null || creditosInsuficientes}
                  className={`w-full ${creditosInsuficientes 
                    ? 'bg-gray-500 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-brand to-purple-500'
                  }`}
                >
                  {generandoAnuncio ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando anuncio...</>
                  ) : creditosInsuficientes ? (
                    <>Créditos insuficientes</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Crear anuncio · {costoTotal} cr</>
                  )}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Voice Clone Modal */}
      <Dialog open={showCloneModal} onOpenChange={setShowCloneModal}>
        <DialogContent className="bg-background border-border-subtle max-w-md">
          <DialogHeader>
            <DialogTitle className="text-text-main flex items-center gap-2">
              <Mic className="w-5 h-5 text-brand" />
              Clonar tu voz
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <p className="text-sm text-text-muted">
              Sube un audio de tu voz (mínimo 30 segundos) para crear una voz clonada que podrás usar en tus avatares.
            </p>
            
            <div>
              <label className="text-sm font-medium text-text-main mb-2 block">Nombre de la voz</label>
              <Input
                value={cloneForm.name}
                onChange={(e) => setCloneForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Mi voz, Voz comercial..."
                className="bg-surface border-border-subtle"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-text-main mb-2 block">Audio de muestra</label>
              <label className="block">
                <div className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                  cloneForm.audioBase64 ? 'border-green-500/50' : 'border-border-subtle hover:border-brand/50'
                }`}>
                  {cloneForm.audioBase64 ? (
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-6 h-6 text-green-400" />
                      <span className="text-sm text-green-400">Audio cargado</span>
                    </div>
                  ) : (
                    <>
                      <Mic className="w-8 h-8 mx-auto text-text-muted mb-2" />
                      <p className="text-sm text-text-muted">MP3, WAV, M4A (30s - 5min)</p>
                      <p className="text-xs text-text-muted mt-1">Habla con claridad, sin ruido de fondo</p>
                    </>
                  )}
                </div>
                <input 
                  type="file" 
                  accept="audio/*" 
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && file.size < 25 * 1024 * 1024) {
                      const reader = new FileReader();
                      reader.onload = (ev) => setCloneForm(prev => ({ ...prev, audioBase64: ev.target?.result }));
                      reader.readAsDataURL(file);
                    } else if (file) {
                      toast.error('El audio debe ser menor a 25MB');
                    }
                  }} 
                  className="hidden" 
                />
              </label>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowCloneModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleCloneVoice}
                disabled={cloningVoice || !cloneForm.name || !cloneForm.audioBase64}
                className="flex-1 bg-gradient-to-r from-brand to-purple-500"
              >
                {cloningVoice ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Clonando...</>
                ) : (
                  <><Star className="w-4 h-4 mr-2" /> Clonar voz · 8 cr</>
                )}
              </Button>
            </div>
            
            <p className="text-xs text-text-muted text-center">
              La clonación tarda ~30 segundos. La voz aparecerá en &quot;Mis voces&quot;.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== IMAGE PREVIEW MODAL (OJITO) ==================== */}
      <Dialog open={anuncioActivo !== null} onOpenChange={(open) => !open && setAnuncioActivo(null)}>
        <DialogContent className="max-w-4xl bg-app-bg border-border-subtle p-0 overflow-hidden">
          {anuncioActivo && (() => {
            const anuncio = anuncios.find(a => a.id === anuncioActivo);
            if (!anuncio) return null;
            
            return (
              <div className="relative">
                {/* Close button */}
                <button 
                  onClick={() => setAnuncioActivo(null)}
                  className="absolute top-4 right-4 z-10 p-2 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
                
                {/* Image/Video Preview */}
                <div className="bg-black flex items-center justify-center min-h-[400px]">
                  {anuncio.tipo?.includes('video') || anuncio.tipo === 'avatar_ia' ? (
                    <video 
                      src={anuncio.creativo_feed_url || anuncio.creativo_story_url}
                      className="max-h-[70vh] w-auto"
                      controls
                      autoPlay
                    />
                  ) : (
                    <img 
                      src={anuncio.creativo_feed_url || anuncio.creativo_story_url}
                      alt={anuncio.headline || 'Creativo'}
                      className="max-h-[70vh] w-auto object-contain"
                    />
                  )}
                </div>
                
                {/* Details panel */}
                <div className="p-6 space-y-4">
                  {/* Headline */}
                  {anuncio.headline && (
                    <div>
                      <span className="text-xs text-text-muted uppercase tracking-wider">Headline</span>
                      <p className="text-lg font-semibold text-text-main mt-1">{anuncio.headline}</p>
                    </div>
                  )}
                  
                  {/* Copy/Copies */}
                  {anuncio.copies && anuncio.copies.length > 0 && (
                    <div>
                      <span className="text-xs text-text-muted uppercase tracking-wider">Copies ({anuncio.copies.length})</span>
                      <div className="mt-2 space-y-3">
                        {anuncio.copies.slice(0, 3).map((copy, idx) => (
                          <div key={copy.id || `copy-${anuncio.id}-${idx}`} className="p-3 bg-surface rounded-lg border border-border-subtle">
                            <p className="text-sm text-text-main">{copy.texto_principal || copy.frase}</p>
                            {copy.estructura && (
                              <span className="inline-block mt-2 text-xs px-2 py-0.5 bg-brand/20 text-brand-400 rounded">
                                {copy.estructura}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex items-center gap-3 pt-4 border-t border-border-subtle">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => descargarCreativo(anuncio.creativo_feed_url, `creativo-${anuncio.id}.jpg`)}
                      className="flex-1"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Descargar Feed
                    </Button>
                    {anuncio.creativo_story_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => descargarCreativo(anuncio.creativo_story_url, `creativo-${anuncio.id}-story.jpg`)}
                        className="flex-1"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Descargar Story
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrafficStep;
